/**
 * Centralized query key factory for TanStack Query.
 *
 * Every query key lives here to avoid string duplication and ensure
 * consistent invalidation across the app.
 */

export const queryKeys = {
  tasks: {
    all: (orgId: string) => ['tasks', orgId] as const,
    archived: (orgId: string) => ['tasks', orgId, 'archived'] as const,
    detail: (taskId: string) => ['tasks', 'detail', taskId] as const,
    fullContext: (taskId: string) => ['tasks', 'full-context', taskId] as const,
    occurrences: (taskId: string) => ['tasks', 'occurrences', taskId] as const,
    // Spec 296 Package C. GET /tasks/create-context, as read by
    // useCreateTask.ts: {members, templates, settings}.
    createContext: (orgId: string) => ['tasks', 'create-context', orgId] as const,
    // Same ENDPOINT as createContext above, deliberately NOT the same key.
    // QuickCreateModal.tsx projects the response into a different shape
    // ({members: normalized, defaults: {assigneeIds, priority,
    // completionPoints}} — no templates/settings), so sharing one key let
    // whichever consumer fetched first serve the other a wrongly-shaped
    // cache entry. That shipped: visiting /tasks/create and then opening the
    // quick-create modal for the same org crashed the app-level
    // ErrorBoundary on `data.defaults` being undefined (prod, 2026-09-07).
    // Own key per distinct projection — same precedent as createDefaults /
    // listDefaults / travelSchedules.orgTimezone below.
    quickCreateContext: (orgId: string) => ['tasks', 'quick-create-context', orgId] as const,
    // Dedicated key for useCreateTask.ts's secondary organizationAPI.getSettings()
    // read (optionalTaskGraceDays helper text only). Deliberately NOT
    // queryKeys.organizations.settings(orgId) — that key used to have multiple
    // incompatible queryFn shapes sharing it, so adding a further shape would
    // have risked this hook silently reading another consumer's wrongly-shaped
    // cache entry. That collision is now FIXED (2026-09-07, bug-registry
    // 0zzz-bd — the projections were split onto nested keys), but this key
    // stays separate on its own merits: it reads a different field for a
    // different purpose and has no reason to share a cache entry.
    createDefaults: (orgId: string) => ['tasks', 'create-defaults', orgId] as const,
    // Structural-debt backlog #8 (pages/tasks/useTasks.ts). ANOTHER
    // organizationAPI.getSettings() reader, projecting yet a third distinct
    // shape (defaultTaskSort/hideCompletedTasks/weekStartsOn -- the task
    // LIST's own display defaults, not createDefaults' optionalTaskGraceDays
    // helper text) -- own dedicated key per this file's established
    // precedent, not a reuse of `createDefaults` or `organizations.settings`.
    listDefaults: (orgId: string) => ['tasks', 'list-defaults', orgId] as const,
    // Spec 296 Package C (useOfflineTasks.tsx). Deliberately its own key,
    // NOT `all` above -- useOfflineTasks hits the plain unfiltered GET
    // /tasks endpoint (no status/sort/search/pagination params) and returns
    // a different shape (isCached/lastSyncTime bundled in), so it can't
    // safely share a cache entry with useTasksQuery.ts's filtered list
    // (see this file's own precedent in organizations.membersForSelect's
    // doc comment for why two different queryFn shapes must never share a
    // key).
    offline: (organizationId: number | undefined) => ['tasks', 'offline', organizationId ?? null] as const,
    // Spec 297 Phase B batch B2 (task-detail/DependencyBanner.tsx). GET
    // /tasks/:id/dependency -- a task's prerequisite relationship, keyed by
    // the DEPENDENT task's id. Own key, not a reuse of `detail`/`fullContext`
    // above (different endpoint, different shape). Prefixed with 'tasks' so
    // it's a descendant of every existing bare `invalidateQueries({queryKey:
    // ['tasks']})` call already used throughout the app -- see this
    // component's own doc comment for why that's an accepted, non-scope-
    // violating side benefit of the migration.
    dependency: (taskId: string) => ['tasks', 'dependency', taskId] as const,
    // Spec 297 Phase B batch B2 (create-task/DependencySection.tsx). Same
    // taskAPI.getAll(orgId, {pageSize:200}) endpoint/params as
    // task-detail/useTaskEditState.ts's own (still-unmigrated, out of
    // scope) `pageSize: 200` fetch -- NOT shared with it (same reasoning as
    // organizations.membersForSelect's doc comment: two independently-
    // maintained call sites sharing a cache key risk drift with no type
    // error to catch it, and useTaskEditState.ts isn't on TanStack yet to
    // even have per-query options to compare). Deliberately a descendant of
    // `all(orgId)` above (not a fully separate top-level key) so existing
    // task-mutation invalidations of `queryKeys.tasks.all(orgId)` (e.g.
    // useTaskDetail.ts's legacy hook) also refresh this picker's options --
    // desired, since a task's status/assignees changing elsewhere should be
    // reflected in the dependency picker too. Does NOT fold assigneeKey into
    // the key: the API call itself never sends an assignee filter (filtering
    // is client-side over the full org task list), so assignee changes don't
    // need a fresh cache entry -- this also fixes a pre-existing inefficiency
    // where the old useCallback's assigneeKey dependency triggered a
    // redundant identical re-fetch on every assignee change.
    dependencyPicker: (orgId: string) => ['tasks', orgId, 'dependency-picker'] as const,
    // Spec 297 Phase B batch B6 (components/helm/TaskPipelineWidget.tsx).
    // GET /tasks/organization/:orgId (taskAPI.getAll) with fixed
    // {pageSize:100, hideCompleted:false} params -- deliberately its own
    // descendant key, NOT a reuse of `all(orgId)` above: useTasksQuery.ts's
    // queryFn (offline caching, pagination, status/sort/search filters) is a
    // different fetch entirely (same reasoning as
    // organizations.membersForSelect's doc comment -- two independently-
    // maintained queryFn bodies must never share a key). Deliberately a
    // DESCENDANT of `all(orgId)` (not a fully separate top-level key) so
    // this widget still gets refreshed by every existing
    // `invalidateQueries({queryKey: queryKeys.tasks.all(orgId)})` call
    // already scattered through the app (useTaskDetail.ts, task-mutation
    // call sites) -- desired, a task's status/assignment changing elsewhere
    // should be reflected here too.
    //
    // userId-scoped: confirmed via backend (taskController.ts's getTasks --
    // this widget hits the SAME organization/:id list endpoint, different
    // params) that the private-task WHERE clause depends on the REQUESTING
    // user's id (`t.created_by_id = $2 OR t.assigned_user_id = $2 OR
    // EXISTS(task_assignees...)`), so the response genuinely varies by
    // viewer within one org, not just by org. Scoped by userId per this
    // batch's mandatory userId-scoping discipline (AuthContext.tsx's
    // login()/register() don't queryClient.clear(), so a same-tab account
    // switch within the same org could otherwise serve a prior user's
    // private-task-filtered list from cache). NOTE: the pre-existing
    // `all(orgId)` root key itself has this SAME gap (bare orgId, no
    // userId) across every already-migrated consumer (useTasksQuery.ts,
    // useTaskDetail.ts, etc.) -- out of scope to fix here (would be a
    // breaking shape change to keys many earlier batches depend on); this
    // mirrors `training.reviewQueue`'s identical "known residual gap, filed
    // not fixed" precedent above.
    pipelineWidget: (orgId: string, userId: string) => ['tasks', orgId, 'pipeline-widget', userId] as const,
    // Spec 297 Phase B batch B6 (components/helm/WeeklyScheduleWidget.tsx).
    // GET /tasks/organization/:orgId/weekly-schedule?weekOf=... -- its own
    // endpoint entirely (weeklyScheduleController.ts), own queryFn shape, so
    // its own key, not a reuse of anything above. weekOf folds into the key
    // (every request-shaping input, mirrors discovery.feed's precedent) so
    // navigating weeks is structurally a fresh cache entry, not a stale
    // useCallback-deps refetch. Deliberately a DESCENDANT of `all(orgId)`
    // (same reasoning as `pipelineWidget` above) so this widget's own
    // `invalidateQueries({queryKey: ['tasks']})` call (kept verbatim from
    // pre-migration -- see WeeklyScheduleWidget.tsx's handleQuickComplete)
    // also refreshes ITS OWN cache entry, replacing the pre-migration
    // `await fetchSchedule()` call with no separate invalidate needed.
    //
    // userId-scoped for the same confirmed reason as `pipelineWidget` above
    // -- weeklyScheduleController.ts's private-task WHERE clause also keys
    // off `req.user.id` ($2), plus an optional `filterUserId` query param
    // this widget never sends.
    weeklySchedule: (orgId: string, weekOf: string, userId: string) =>
      ['tasks', orgId, 'weekly-schedule', weekOf, userId] as const,
    // Spec 297 Phase B batch B7 (CompletionDisplay.tsx). GET
    // /tasks/:taskId/completions -- task-shared row data (any org member
    // with task access sees the same completions), BUT the server rewrites
    // each completion's files[]/file_path to a PER-VIEWER signed proxy URL
    // (completionMedia.ts's proxyCompletionFileRows(rows, requesterId) ->
    // buildProxyUrl(fileId, requesterId, 'completion'), an HMAC token with
    // the requester's userId baked in, verified at proxy-fetch time
    // independent of the viewing browser's own session -- confirmed by
    // reading mediaProxyUrl.ts/completionMedia.ts directly). Scoped by
    // userId: without it, AuthContext's login()/register() not clearing the
    // QueryClient means a same-tab account switch could render a PRIOR
    // user's still-cached signed URL, and that URL would actually resolve
    // (as the prior user, not the new session) rather than merely 404 --
    // a live-exploitable cross-user media exposure, not just a stale-read.
    completions: (taskId: string, userId: string) => ['tasks', taskId, 'completions', userId] as const,
    // Spec 297 Phase B batch B8 (components/Task/RewardsList.tsx). GET
    // /tasks/:taskId/rewards (rewardAPI.listTaskRewards) -- its own endpoint
    // (taskRewardRoutes.ts's getRewardsForTask), own queryFn shape, so its
    // own key, not a reuse of `rewards.mine(userId)` (Phase B batch B2 --
    // that's the RECIPIENT's cross-task "my rewards" page, a different
    // endpoint/response shape entirely).
    //
    // userId-scoped: confirmed via backend (rewardController.ts's
    // getRewardsForTask) that the response genuinely varies by viewer within
    // the SAME task -- task creator/org-admin see every reward on the task,
    // a plain assignee sees only the rewards recipient-addressed to
    // themselves (`getRecipientRewards(userId, taskId)` vs
    // `getTaskRewards(taskId, userId)`). Scoped by userId per this batch's
    // mandatory discipline (AuthContext.tsx's login()/register() don't
    // queryClient.clear(), so a same-tab account switch viewing the same
    // taskId could otherwise serve a prior user's role-filtered reward list
    // from cache -- e.g. an admin's full list leaking to a subsequently
    // logged-in assignee).
    rewards: (taskId: string, userId: string) => ['tasks', 'rewards', taskId, userId] as const,
  },
  // Public marketing blog (BlogListPage.tsx/BlogPostPage.tsx). Deliberately its
  // own top-level namespace, not nested under `admin.marketing.blog` (the CMS
  // list/detail keys for drafts/review) -- these are unauthenticated public
  // reads of PUBLISHED posts only, a completely different query/cache surface
  // with no shared invalidation need.
  blog: {
    list: (params: { page: number; category: string }) => ['blog', 'list', params] as const,
    post: (slug: string) => ['blog', 'post', slug] as const,
  },
  inventory: {
    // Spec 296 Package C (InventoryTableSelector.tsx). assigneeKey is the
    // sorted/joined assignee-userId string (stable identity for the assignees
    // array prop, mirrors the component's pre-existing assigneeKey memo).
    forTask: (assigneeId: string | undefined, assigneeKey: string, collection?: string) =>
      ['inventory', 'for-task', assigneeId ?? null, assigneeKey, collection ?? null] as const,
  },
  games: {
    // Spec 296 Package C (TaskGame.tsx). Single combined query — config,
    // hasPlayed, latest result, and rolled-requirement are always fetched
    // together and rendered together; see TaskGame.tsx's docstring for why
    // this is one query rather than four.
    taskGame: (taskId: string) => ['games', 'task-game', taskId] as const,
    // Spec 297 Phase B batches B7 (CoinFlip.tsx / DiceRoll.tsx) and B8
    // (SpinnerWheel.tsx, added independently in a worktree cut before B7
    // landed -- confirmed byte-for-byte identical key shape at merge, no
    // reconciliation needed beyond this comment consolidation). GET
    // /games/task/:taskId/game -- the "visual persistence" game state
    // (survives navigation before/after accept), a DIFFERENT endpoint from
    // `taskGame` above (TaskGame.tsx's own config/hasPlayed/latest-result/
    // rolled-requirement bundle, which records the actual scored outcome via
    // /roll-dice /flip-coin /spin-wheel). Deliberately NOT scoped by userId:
    // taskGameService.getGameState(taskId) reads `task_game_state WHERE
    // task_id = $1` -- one row per TASK, returned as-is with no per-viewer
    // transformation (confirmed by reading taskGameRoutes.ts's GET
    // /task/:taskId/game handler and the service function directly) -- every
    // org member with task access sees the identical row, not "my own"
    // data.
    // UPDATED (bug-registry.md follow-up to 52aa2e70a -- multi-assignee
    // unscoped game-result exposure): GET /task/:taskId/game is now scoped
    // server-side to the requesting user (getGameStateForUser, WHERE
    // played_by_user_id = req.user.id) so a co-assignee who hasn't played
    // no longer sees another assignee's played-but-not-yet-accepted roll.
    // The query key below still doesn't include userId -- that remains safe
    // because a QueryClient instance lives for exactly one authenticated
    // user per browser session (no user-switching without a full app
    // reload/logout), not because the underlying data is task-shared
    // anymore. Shared by CoinFlip.tsx, DiceRoll.tsx, and SpinnerWheel.tsx:
    // safe, since TaskGame.tsx renders at most ONE of the three for a given
    // taskId (branches on config.gameType), so they never collide on the
    // same key for the same task.
    state: (taskId: string) => ['games', 'state', taskId] as const,
  },
  activePops: {
    // Spec 296 Package C (ActivePopBanner.tsx). Two distinct endpoints
    // (single-task vs. all-mine) get distinct keys, matching the component's
    // existing `taskId ? getActivePops(taskId) : getMyPendingPops()` branch.
    forTask: (taskId: string) => ['active-pops', 'task', taskId] as const,
    mine: () => ['active-pops', 'mine'] as const,
  },
  consequences: {
    // Spec 296 Package C (ResponsePalette.tsx reject mode).
    templates: () => ['consequences', 'templates'] as const,
    // Structural-debt backlog #8 (pages/consequences/useConsequences.ts).
    // consequenceAPI.getMyActive/getMyHistory/getStats take no orgId param,
    // but ARE genuinely org-scoped -- services/api/client.ts's interceptor
    // attaches an X-Organization-Id header from the active org, so these
    // vary per open org, not just per caller. Both userId (cross-account-
    // switch) and orgId (cross-org-switch) scoping needed, unlike the
    // userId-only storageManagementAdmin keys above (whose backend resolves
    // "the caller's org" independent of which org is open).
    myActive: (userId: string, orgId: string) => ['consequences', 'my-active', userId, orgId] as const,
    myHistory: (userId: string, orgId: string) => ['consequences', 'my-history', userId, orgId] as const,
    stats: (userId: string, orgId: string) => ['consequences', 'stats', userId, orgId] as const,
  },
  taskTemplates: {
    // Spec 296 Package C (useTaskTemplates.ts). ONE key for the combined
    // browse fetch (GET /task-templates + GET /task-templates/categories via
    // Promise.all) — the pre-migration code fetches and error-handles them
    // as a single unit (either both resolve or the shared catch fires for
    // both), so this stays one query rather than two independent ones, which
    // would let one endpoint's failure surface without the other's.
    browse: (orgId: string | undefined, category: string | null) =>
      ['task-templates', 'browse', orgId ?? null, category] as const,
  },
  helm: {
    // SAFETY FIX (found during the Spec 297 Phase B integration checkpoint's
    // qa-fanout sweep, a 7th/8th live instance of this session's recurring
    // cross-user cache-leak class -- an explicit unclosed lead from batch
    // B5's own review, see the widgets key's comment below). Was org-scoped
    // only. helmController.getHelmData resolves the caller's role
    // (dominant vs submissive) via getUserRole(userId, orgId) and dispatches
    // to a STRUCTURALLY DIFFERENT payload (getDominantHelm vs
    // getSubmissiveHelm) -- not just a filtered list. Combined with
    // AuthContext's login()/register() not calling queryClient.clear(), a
    // same-tab account switch could render a prior user's entire
    // role-specific dashboard. Now userId-scoped. Invalidation-only call
    // sites (mutations elsewhere that don't themselves read this data) use
    // the raw ['helm-dashboard', orgId] prefix instead of importing useAuth
    // -- invalidateQueries prefix-matches by default, same pattern already
    // established by refreshWidgets in HelmDataContext.tsx.
    dashboard: (orgId: string, userId: string) => ['helm-dashboard', orgId, userId] as const,
    // SAFETY FIX (Spec 297 Phase B batch B5's own skeptical review). Was
    // org-scoped only. helmWidgetService.ts's
    // getMessagesWidgetData(userId, orgId) filters `WHERE cp.user_id = $1`
    // -- the caller's own DM conversation previews + unread counts, not
    // shared org data. Combined with AuthContext's login()/register() not
    // calling queryClient.clear(), a same-tab account switch could render a
    // prior user's private conversation names. Now userId-scoped; the 3 live
    // consumers (HelmDataContext.tsx, DiscoveryWidget.tsx's and
    // MessagesWidget.tsx's standalone fallback paths, all via the shared
    // useHelmWidgets hook) all pick this up automatically since they route
    // through one hook.
    widgets: (orgId: string, userId: string) => ['helm-widgets', orgId, userId] as const,
    // SAFETY FIX (same checkpoint sweep as `dashboard` above -- the sibling
    // finding). Was org-scoped only. helmReadStatusService.ts's
    // getUnreadCounts(userId, orgId) filters per-caller
    // (`WHERE ta.user_id = $1` on the submissive path, an equivalent filter
    // on the dominant path) -- genuinely per-viewer unread counts, not
    // org-shared. Now userId-scoped.
    unreadCount: (orgId: string, userId: string) => ['helm-unread', orgId, userId] as const,
    // Spec 296 Package B — AttentionStrip.tsx's cross-device snooze sync
    // (distinct from the bulk dashboard payload above: its own
    // GET .../helm/attention/snooze-status endpoint).
    attentionSnooze: (orgId: string) => ['helm-attention-snooze', orgId] as const,
    // Spec 296 Package B — HelmOverviewPromptCard.tsx's eligibility check
    // (GET .../helm/overview-prompt).
    overviewPrompt: (orgId: string) => ['helm-overview-prompt', orgId] as const,
    // Spec 297 Phase B batch B5 (BehavioralInsightsPanel.tsx). GET
    // .../helm/insights?periodDays=N&userId=X -- a Dominant can view any
    // partner's data via the userId filter, but a member/sub's own request
    // always returns their own regardless of what's passed (see
    // backend/src/routes/helmRoutes.ts's getInsights handler: non-dominant
    // callers get `effectiveUserId = req.user.id` unconditionally). The
    // DEFAULT (no filter) branch returns the CALLER's own data, so this key
    // is scoped by the VIEWING user's id (viewerId) -- without it, a
    // non-logout account switch in the same tab could surface a stale prior
    // viewer's own-insights under this component's no-filter default state
    // (same root cause as `negotiations.active`'s SAFETY FIX comment above).
    // targetUserId (the explicit per-sub filter dropdown) also folds into
    // the key so switching the filter is structurally a fresh cache entry.
    insights: (orgId: string, viewerId: string, periodDays: number, targetUserId?: string) =>
      ['helm-insights', orgId, viewerId, periodDays, targetUserId ?? null] as const,
  },
  // Spec 296 Package B — FeatureDiscoveryCard.tsx. Global (not org-scoped):
  // GET /feature-visits/discovery returns the next unvisited feature for the
  // caller regardless of which organization is selected.
  featureDiscovery: {
    card: () => ['feature-discovery-card'] as const,
  },
  // Spec 296 Package B — HelmWelcome.tsx's org-independent GET
  // /users/identity fetch. Distinct from hooks/useIdentity.ts (a
  // localStorage/org-role resolution chain, not a query) — same domain
  // concept, different mechanism, so kept out of that hook's naming.
  identity: {
    self: () => ['user-identity'] as const,
    // Spec 297 Phase A batch 8 (useRegister.ts's resume-hydration effect).
    // Same GET /users/identity endpoint (authAPI.getIdentity) as `self`
    // above, via useUserIdentityQuery.ts -- but deliberately NOT a reuse of
    // that hook/key: its `isResolvedRole()` projection silently drops the
    // 'exploring' value (returns null for it), which is correct for its own
    // HelmWelcome.tsx consumer but wrong here -- useRegister.ts's `Identity`
    // type is 'dominant'|'submissive'|'switch'|'exploring', and a resuming
    // user who originally picked 'exploring' must hydrate that value, not
    // null. Confirmed by reading useUserIdentityQuery.ts's isResolvedRole()
    // before reusing it -- sharing would have silently regressed
    // 'exploring' users on resume. A fully distinct top-level key (not
    // nested under `self`'s ['user-identity'] prefix) so a future
    // `invalidateQueries({queryKey: identity.self()})` can never
    // accidentally cascade into this cache entry too.
    //
    // SAFETY FIX (2026-08-04, `helm-safety-reviewer` HIGH found during
    // batch 8's review, before this batch ever landed): originally a bare
    // key. Pre-migration this fetch's result lived in a useState local to
    // the useRegister hook instance, discarded on unmount -- moving it into
    // the app-level QueryClient singleton (persists ~5min gcTime / 30s
    // staleTime across mounts in the same tab) turned it into a genuine
    // cross-user leak: AuthContext.tsx's login() does NOT call
    // queryClient.clear() (only logout()/handleAuthError() do), and
    // useRequireOnboarding.ts's redirect into /register is a client-side
    // navigate(), not a full reload -- so User A registers, abandons before
    // completing onboarding, and later User B logs into the SAME TAB
    // (shared/public computer, or a QA machine cycling test accounts) and
    // gets routed into /register's resume flow too. Without user-scoping,
    // B's identityHydrationQuery could read A's still-cached identity
    // selection from a bare key -- and since the hydrate-once ref guard is
    // a one-shot latch, it would apply A's value to B's form and never
    // re-check. Scoped by userId below so two different users' resume
    // sessions in one tab can never collide on this cache entry.
    registrationResume: (userId: string) => ['user-identity-registration-resume', userId] as const,
  },
  // Spec 296 Package B — HiddenNotificationsPromptCard.tsx's GET/PUT
  // /users/me/mobile-privacy. Also consumed by useAccountSettings.ts today
  // (hand-rolled, not migrated — out of this spec's scope); same key shape
  // so a future migration there can reuse it.
  mobilePrivacy: {
    self: () => ['mobile-privacy'] as const,
  },
  // Structural-debt backlog #8 (account-settings/useAccountSettings.ts).
  // Own dedicated key -- deliberately NOT queryKeys.notifications.preferences()
  // (hooks/queries/useNotifications.ts): that hook's queryFn returns the raw
  // unwrapped response with no `.preferences` extraction, but the real
  // notificationAPI.getPreferences() response is `{preferences: {...}}`
  // (confirmed via useAccountSettings.dmPrivacy.test.ts's mock and this
  // file's own pre-migration `.data.preferences` access) -- that existing
  // hook has zero real consumers and its own test mocks an inconsistent
  // flat shape, so reusing it would silently read the wrong fields here.
  accountSettings: {
    notificationPreferences: () => ['account-settings', 'notification-preferences'] as const,
    // mediaAPI.getBillingStatus() alone -- NOT mediaBilling.status(userId)
    // above, which bundles status+plans+trial via Promise.all (different
    // queryFn shape, see that key's own doc comment).
    mediaBillingStatus: () => ['account-settings', 'media-billing-status'] as const,
    mySafewords: () => ['account-settings', 'my-safewords'] as const,
    dmPrivacy: () => ['account-settings', 'dm-privacy'] as const,
    deletionStatus: () => ['account-settings', 'deletion-status'] as const,
    reminderFrequency: () => ['account-settings', 'reminder-frequency'] as const,
  },
  compliance: {
    summary: (orgId: string, period: string) => ['helm-compliance-summary', orgId, period] as const,
  },
  organizations: {
    // GET /organizations/:orgId/settings, RAW `{settings: ...}` envelope as
    // returned by the API — useOrgSettings.ts's projection, and the only one
    // that keeps the bare key.
    //
    // Two other consumers read the SAME endpoint but project it into shapes
    // that are not interchangeable with this one, so they get their own keys
    // below rather than sharing this entry. They are deliberately NESTED
    // UNDER this key's array: `invalidateQueries({queryKey: settings(orgId)})`
    // matches by PREFIX (TanStack Query's default is exact: false), so the two
    // existing invalidations — useOrgSettings.ts's useUpdateOrgSettings and
    // useGeneralSettings.ts's save — still refresh all three projections
    // without needing to know about them. Do NOT flatten these into siblings.
    settings: (orgId: string) => ['org-settings', orgId] as const,
    // useGeneralSettings.ts — parseSettings() -> {taskSettings, tagsInput}.
    settingsGeneralForm: (orgId: string) => ['org-settings', orgId, 'general-form'] as const,
    // useSceneComposer.ts — a bare `number | null` (sceneIntensityBudget).
    //
    // This one is why the split happened rather than being left as a filed
    // GAP. It declares `useQuery<number | null>`, and the scene composer gates
    // its intensity guard on `intensityBudget != null && intensityTotal >
    // intensityBudget` (useSceneComposer.ts). Served one of the OBJECT shapes
    // above from the shared key, `!= null` is true and `number > object` is
    // always false — so the over-budget guard silently never fires and the
    // meter renders "N / [object Object]". Both object consumers use
    // staleTime: 300_000, so opening org settings and then the scene composer
    // within 5 minutes was enough. A quiet failure of a consent-surface
    // guardrail, not a loud one. See bug-registry 0zzz-bd.
    settingsSceneIntensity: (orgId: string) => ['org-settings', orgId, 'scene-intensity'] as const,
    members: (orgId: string) => ['org-members', orgId] as const,
    // Structural-debt backlog #8. JoinOrganizationPage.tsx's invite-preview
    // fetch (organizationAPI.getInviteDetails) -- an unauthenticated, public
    // per-invite-code lookup, not a per-org one, but namespaced here since
    // it's organization-domain data.
    inviteDetails: (inviteCode: string) => ['org-invite-details', inviteCode] as const,
    // Spec 294 follow-up (2026-08-03): dedicated key for
    // useOrganizationMembers.ts (SelectOption-mapped member list for
    // MemberMultiSelect.tsx / ShareSheet.tsx). Deliberately NOT the same
    // key as `members` above -- it used to intentionally share that key
    // with useOrgMembersQuery (hooks/queries/useOrgMembers.ts), reasoning
    // that both hit the identical organizationAPI.getMembers(orgId)
    // endpoint with an identical response shape. The DATA shape matched,
    // but the two hooks set different per-Query options on the shared
    // observer (this hook wants `retry: false`; useOrgMembersQuery sets
    // `staleTime: 300_000` for its own, unrelated consumer,
    // useSettingsCore.ts) -- TanStack Query has exactly one options set per
    // cache entry, so whichever hook mounted first silently won for both,
    // a dormant footgun if the two are ever co-mounted. Splitting the key
    // removes it entirely. useAddOrgMember/useUpdateMemberRole/
    // useRemoveOrgMember (useOrgMembers.ts) invalidate BOTH this key and
    // `members` on every mutation, so this hook keeps getting invalidated
    // for free too -- not every consumer is a fresh-mount-on-open selector
    // (ShareSheet.tsx can stay mounted with its share panel merely slid
    // off-screen via CSS transform, see Sheet.tsx's `shareConfig` prop),
    // so the free invalidation coupling is kept even though the options
    // are now isolated.
    membersForSelect: (orgId: string) => ['org-members-for-select', orgId] as const,
    // Spec 297 Phase A batch 5. StartNegotiationModal.tsx's partner picker --
    // organizationAPI.getMembersOnly(orgId), a different endpoint/response
    // shape than getMembers() (`members` above) or getMembersOnly's other
    // still-unmigrated callers (QuickPraise.tsx, CreateProtocolPage.tsx,
    // useOrgasmBank.ts, NegotiationsTab.tsx) -- none of those are on TanStack
    // yet, so there's no existing key for this endpoint to reuse.
    membersOnly: (orgId: string) => ['org-members-only', orgId] as const,
    categories: () => ['org-categories'] as const,
    health: (orgId: string) => ['org-health', orgId] as const,
    // Spec 297 Phase A batch 6 (FeaturesTab.tsx). GET
    // /organizations/:orgId/available-features -- the org-scoped feature
    // CATALOG (every catalog feature + this org's current on/off state),
    // distinct from `features.all` below (FeaturesContext's bare GET
    // /features, a different endpoint returning the caller's merged runtime
    // flag set for nav/gating). FeaturesTab.tsx's existing bare
    // `invalidateQueries({queryKey: ['features']})` call (pre-dating this
    // migration, see `features.all`'s doc comment) is UNCHANGED by this key
    // -- it targets FeaturesContext's cache on purpose so nav/gating picks
    // up a saved override immediately; this key is for the catalog LIST
    // itself, which the save mutation instead writes directly via
    // `setQueryData` from the server's returned catalog (mirrors
    // SafetyCheckinPage.tsx's setQueryData-over-invalidate precedent).
    availableFeatures: (orgId: string) => ['org-available-features', orgId] as const,
    // Spec 297 Phase A batch 6 (DynamicTab.tsx). GET
    // /organizations/:orgId/keyholder-assignments -- sole consumer, no
    // known collision.
    keyholderAssignments: (orgId: string) => ['org-keyholder-assignments', orgId] as const,
    // Caller's own hierarchy list (organizationAPI.getAll()). Shared by
    // useOrganizations.ts's list-page load and its post-mutation invalidate.
    myList: () => ['org-my-list'] as const,
    // Spec 297 Phase A batch 8 (useRegister.ts's resume-target-resolution
    // effect). Same organizationAPI.getAll() endpoint/shape as myList()
    // above -- originally reused that key directly (same endpoint AND
    // options), but split into its own, userId-scoped key per
    // `helm-safety-reviewer`'s review of this batch (same root cause as
    // `identity.registrationResume()`'s doc comment above: AuthContext's
    // login() doesn't clear the QueryClient, and the /register resume path
    // is reachable via a client-side navigate from an ordinary login, so a
    // bare shared key could serve a DIFFERENT user's cached org list to a
    // resuming user in the same tab -- here that would misroute the
    // member-vs-admin step decision, or wrongly skip hierarchy/org creation
    // because a stale org appeared to already exist). Scoping to the
    // register flow's own key (rather than adding userId to myList() itself)
    // keeps this fix local to the register flow instead of changing
    // myList()'s shape for its existing useOrganizations.ts consumer. A
    // fully distinct top-level key (not a prefix-descendant of myList()'s
    // ['org-my-list']) so a future `invalidateQueries({queryKey: myList()})`
    // can't accidentally cascade into every cached user's resume-flow entry.
    registrationResumeOrgList: (userId: string) => ['org-my-list-registration-resume', userId] as const,
    // Spec 294 Checkpoint 2 (OrganizationContext.tsx). Same underlying
    // endpoint/response shape as myList() above -- a DELIBERATELY separate
    // key, not a reuse. Two reasons, in priority order:
    //   1. Side-effect surface: OrganizationContext reacts to every fetch of
    //      this data with an effect that can auto-select a single org (see
    //      that file's "3 refreshFromAPI branches" effect). useOrganizations.ts's
    //      create/join mutations invalidate myList() on success -- sharing
    //      the key would mean those mutations silently trigger
    //      OrganizationContext's auto-select-if-one-org logic too, which
    //      would fire on a deliberate create-WITHOUT-select path
    //      (useCreateOrganization's `{ select: false }` option exists
    //      specifically so creating a hierarchy doesn't switch the user's
    //      active org -- see useOrganizationBootstrap.ts). Pre-migration,
    //      OrganizationContext's org list and useOrganizations.ts's org list
    //      were fully independent (separate component state, never
    //      cross-invalidated) -- splitting the key preserves that
    //      independence instead of introducing new coupling.
    //   2. Per-query options: OrganizationContext is the app-root, long-lived
    //      Provider and wants staleTime: Infinity / refetchOnWindowFocus:
    //      false (matching every other TanStack-migrated Context in this
    //      track), while useOrganizations.ts's page-scoped query relies on
    //      the global QueryClient defaults. Same shape as the
    //      members/membersForSelect split above.
    // If a mutation ever needs to keep BOTH lists fresh together, invalidate
    // both keys explicitly at the call site -- don't merge them back into one.
    contextList: () => ['org-context-list'] as const,
    // Public browse/discovery list (onboarding + organizations pages share
    // this shape: search/category/locationType/sort filters).
    publicList: (params: { search?: string; category?: string; locationType?: string; sort?: string }) =>
      ['org-public-list', params] as const,
    // Spec 297 Phase B batches B10+B11 (OrganizationInviteSelector.tsx). GET
    // /organizations/with-invite-status -- confirmed via
    // organizationController.ts's listOrganizationsWithInviteStatus:
    // `getOrganizationsWithInviteStatus(userId)` is keyed entirely off the
    // CALLER's own id, no org-scoping param at all -- "which hierarchies do
    // I (the caller) belong to, and what's each one's partner-invite state."
    // Deliberately NOT a reuse of `myList` above -- different endpoint,
    // different response shape (adds `hasPartner`/`pendingInvites` per org),
    // same reasoning as `registrationResumeOrgList`'s split from `myList`.
    // Scoped by userId per this batch's mandatory discipline: AuthContext's
    // login()/register() don't queryClient.clear(), so a same-tab account
    // switch could otherwise serve a prior user's own hierarchy-membership
    // list from cache.
    withInviteStatus: (userId: string) => ['org-with-invite-status', userId] as const,
  },
  dashboard: {
    preferences: (role: string) => ['dashboard-preferences', role] as const,
  },
  // GET /analytics/dashboard (PerformanceDashboardPage.tsx). Org-scoped
  // (analyticsAPI.getDashboard(organizationId, period)) + period-scoped
  // (every request-shaping input, per this file's own discipline).
  analytics: {
    dashboard: (organizationId: string, period: string) => ['analytics', 'dashboard', organizationId, period] as const,
  },
  points: {
    config: () => ['points-config'] as const,
    // Spec 296 Package D. Prefix ('points') deliberately matches the
    // pre-existing (previously inert) `queryClient.invalidateQueries({
    // queryKey: ['points'] })` calls already in ResponsePalette.tsx and
    // useStore.ts (both out of this package's scope) — those calls predate
    // this hook's migration and had nothing to invalidate; using the same
    // prefix here means they now correctly reach this cache.
    all: (orgId: string) => ['points', orgId] as const,
    // userId-scoped (2026-08-06, bug-registry.md GAP filed 2026-08-04):
    // this key bundles the caller's OWN balance/transactions/purchases
    // (not just the org-shared leaderboard) but was originally scoped only
    // by orgId — the same class of bug `streaks.dashboard` (below) closed.
    // The live same-tab cross-user cache-bleed this enabled is already
    // closed structurally by AuthContext.tsx's login()/register() now
    // calling queryClient.clear() (see bug-registry.md's 2026-08-05 FIXED
    // entry) — this userId scoping is defense-in-depth cleanup, not a
    // live-bug fix, matching that entry's own "Update" note.
    dashboard: (orgId: string, leaderboardPeriod: string, userId: string) =>
      ['points', orgId, 'dashboard', leaderboardPeriod, userId] as const,
    // Structural-debt backlog #8 (command/useStore.ts's Sub Store tab).
    // Bundles getBalance+getStoreItems+getTransactions({limit:20}) --
    // exact same 3 endpoints/limit as points.dashboard's own bundle above,
    // but deliberately its OWN key rather than reusing dashboard(): that
    // key also takes leaderboardPeriod/userId params this consumer has no
    // reason to plumb through, and bundles purchases+leaderboard data this
    // consumer never reads. Nested under the `points`/orgId prefix so the
    // existing bare `['points']` and `points.all(orgId)` invalidation
    // calls (already in this file, see `all`'s own comment) keep reaching
    // it for free.
    storeSummary: (orgId: string) => ['points', orgId, 'store-summary'] as const,
  },
  // Spec 296 Package D. Prefix ('orgasm-bank') matches the pre-existing
  // `queryClient.invalidateQueries({ queryKey: ['orgasm-bank'] })` call in
  // useStore.ts (out of scope for this package).
  orgasmBank: {
    // Bare (no orgId) so it exactly matches the pre-existing
    // `queryKey: ['orgasm-bank']` invalidation call/assertion (useStore.ts,
    // useOrgasmBank.test.ts) -- still a valid prefix of every keyed entry
    // below, so invalidation still reaches them all.
    all: () => ['orgasm-bank'] as const,
    enabled: (orgId: string) => ['orgasm-bank', orgId, 'enabled'] as const,
    balance: (orgId: string) => ['orgasm-bank', orgId, 'balance'] as const,
    grantedBalances: (orgId: string) => ['orgasm-bank', orgId, 'granted'] as const,
    transactions: (orgId: string) => ['orgasm-bank', orgId, 'transactions'] as const,
    // Structural-debt backlog #8 (command/useStore.ts's Sub Store tab).
    // getTransactions({limit:10}) -- DIFFERENT limit than `transactions`
    // above (limit:50, useOrgasmBank.ts's own page), so a different
    // queryFn shape needs its own key per this file's usual precedent
    // (see organizations.membersForSelect's doc comment). Still nested
    // under the bare `orgasm-bank` prefix for the same free-invalidation
    // reason as `all` above.
    storeTransactions: (orgId: string) => ['orgasm-bank', orgId, 'store-transactions'] as const,
  },
  // Spec 296 Package D. Prefix ('protocols') matches useProtocols.ts's own
  // pre-existing `queryClient.invalidateQueries({ queryKey: ['protocols'] })`
  // call (unchanged by this migration, now a real invalidation target).
  protocols: {
    // Bare (no orgId) -- matches the pre-existing exact
    // `queryKey: ['protocols']` invalidation call/assertion.
    all: () => ['protocols'] as const,
    categories: (orgId: string) => ['protocols', orgId, 'categories'] as const,
    list: (orgId: string, filters: { categoryId: number | null; importance: string | null }) =>
      ['protocols', orgId, 'list', filters] as const,
    mine: (orgId: string) => ['protocols', orgId, 'my'] as const,
    // Spec 297 Phase A batch 5. AdoptProtocolModal.tsx's picker calls
    // getProtocols({ activeOnly: true }) -- a different filter shape than
    // `list` above ({categoryId, importance}, ProtocolsPage's own filters).
    // Deliberately a separate key, not a reuse of `list` with those filters
    // left null: reusing it would collide a `activeOnly:true` fetch with
    // ProtocolsPage's unfiltered 'all' tab under the exact same cache entry.
    adoptable: (orgId: string) => ['protocols', orgId, 'adoptable'] as const,
    // Spec 297 Phase B batch B3 (ProtocolDetailPage.tsx). Bundles GET
    // /protocols/:id + (if admin) getAssignments(id) + GET /protocols/:id/share
    // into ONE query, mirroring safeword.dashboard/collaring.data's
    // precedent of bundling a page's whole on-mount load into one cache
    // entry. `isAdmin` folds into the key (same reasoning as
    // safeword.dashboard's isAdmin-in-key precedent above) -- it changes
    // both which sub-fetches fire AND the response shape (a non-admin never
    // sees assignments/share state). Still a descendant of the bare
    // `protocols.all` (`['protocols']`) prefix, so the existing
    // `invalidateQueries({queryKey:['protocols']})` calls (this page's own
    // handleSave/handleDelete, CreateProtocolPage.tsx, useProtocols.ts's
    // mutations) all reach it.
    detail: (id: string, isAdmin: boolean) => ['protocols', 'detail', id, isAdmin] as const,
    // Spec 297 Phase B batch B9 (LapseReportModal.tsx). Bundles GET
    // /protocols?activeOnly=true&limit=100 + a per-protocol GET
    // /protocols/:id/assignments (both admin-only, requireAdmin-gated) into
    // ONE composite query -- the picker's own "assignee -> protocol"
    // dropdown data. Extra scrutiny applied (consent/safety-adjacent: this
    // feeds a lapse report against another member). Deliberately NOT
    // userId-scoped: getAssignments' service does `requireAdmin` then reads
    // by protocolId/orgId only (backend/src/controllers/protocolController.ts
    // getAssignments -> protocolService.getProtocolAssignments(protocolId),
    // no per-caller filter) -- every admin of the org sees the IDENTICAL
    // assignee list for a given protocol, so this is shared org/admin data,
    // not "my own" state, matching protocols.list/protocols.adoptable's
    // org-only precedent rather than the userId-scoped SAFETY FIX pattern
    // used elsewhere in this file for genuinely per-caller data.
    lapseCandidates: (orgId: string) => ['protocols', orgId, 'lapse-candidates'] as const,
    // ProtocolsTabV2.tsx's DomView. GET /protocols/lapse-reports?acknowledged=false&limit=10 --
    // org-scoped (requireOrganization on protocolRoutes, per this file's own
    // comment at the call site).
    lapseReports: (orgId: string) => ['protocols', orgId, 'lapse-reports'] as const,
  },
  // Spec 297 Phase B batch B9 (TemplateLibrary.tsx / TemplateEditModal.tsx).
  // GET /protocols/templates -- confirmed org-scoped, not per-user, via
  // protocolTemplateService.getTemplates(orgId): `WHERE pt.is_active = true
  // AND (pt.is_system = true OR pt.organization_id = $1)` -- system templates
  // plus this org's own forked/created templates, IDENTICAL for every member
  // of the org. No userId needed (same reasoning as protocols.lapseCandidates
  // above). Deliberately kept under the literal 'protocol-templates' prefix
  // (NOT folded into the `protocols` namespace above, which uses the
  // 'protocols' literal) because TemplatePreviewModal.tsx (unmigrated this
  // batch) and this batch's own TemplateEditModal.tsx/TemplateLibrary.tsx all
  // call the pre-existing exact `invalidateQueries({queryKey:
  // ['protocol-templates']})` -- TanStack's prefix-match invalidation only
  // reaches this query if its key starts with that same literal. `all()`
  // mirrors protocols.all()'s "bare prefix, matches existing exact
  // invalidation call" pattern above.
  protocolTemplates: {
    all: () => ['protocol-templates'] as const,
    list: (orgId: string) => ['protocol-templates', orgId, 'list'] as const,
  },
  // Spec 296 Package D. Distinct namespace/endpoint from `compliance.summary`
  // above (that one is 'helm-compliance-summary', the Helm dashboard widget's
  // own endpoint) — ComplianceTab.tsx reads protocolAPI.getCompliance()/
  // getComplianceSummary()/getComplianceHeatmap(), a different data source.
  protocolCompliance: {
    dashboard: (periodDays: number) => ['protocol-compliance', 'dashboard', periodDays] as const,
    heatmap: (assignmentId: number, periodDays: number) => ['protocol-compliance', 'heatmap', assignmentId, periodDays] as const,
    // Spec 297 Phase A batch 5. ProtocolComplianceCard.tsx's own-compliance
    // readout -- getUserCompliance(userId, {periodDays}), the
    // correctly-scoped (self-or-admin) sibling of the admin-only getCompliance()
    // dashboard above (see docs/reference/bug-registry.md's 2026-07-19 fix).
    //
    // bug-registry.md "queryKeys.ts namespaces omit orgId" GAP fix
    // (2026-08-06): added orgId even though the global organizationChanged
    // listener (queryClient.ts's removeQueries() on a real org-id change)
    // already prevented this from being live-exploitable -- this closes the
    // latent structural gap so a future narrowing of that global wipe can't
    // silently reopen a cross-org leak here.
    user: (orgId: string, userId: string | number, periodDays?: number) => ['protocol-compliance', 'user', orgId, userId, periodDays] as const,
    // ProtocolsTabV2.tsx's SubView. GET /protocols/compliance?periodDays=30 --
    // same endpoint as `dashboard` above, but a DIFFERENT queryFn shape
    // (single unwrapArray(res.data), not dashboard's bundled
    // {complianceData, summary} composite) -- per this file's own
    // "a different queryFn body can't safely share a cache key" precedent
    // (see mediaLibrary.billing's doc comment), this gets its own key rather
    // than reusing `dashboard`.
    forSubView: (periodDays: number) => ['protocol-compliance', 'sub-view', periodDays] as const,
  },
  // Spec 297 Phase A batch 5. NegotiationStatusCard.tsx / StartNegotiationModal.tsx
  // (training-programs feature_action tasks) -- same limitsAPI.getActiveNegotiation()/
  // getNegotiationResult() endpoints web/src/pages/checklist/NegotiationsTab.tsx
  // also calls, but that page is still hand-rolled useState/useEffect (not yet
  // migrated), so there is no existing TanStack key to reuse here.
  negotiations: {
    // SAFETY FIX (2026-08-04, Spec 297 Phase A post-merge fanout review,
    // MEDIUM x12 -- design debt, not currently live-exploitable, confirmed
    // by 3 independent review passes): originally a bare key. Same root
    // cause as `identity.registrationResume`'s SAFETY FIX comment above --
    // AuthContext's login()/register() don't queryClient.clear() -- applied
    // here as defense-in-depth hardening across every other genuinely
    // per-user cache entry this migration introduced. Scoped by userId so
    // two different users' sessions in one tab can never collide on this
    // cache entry.
    // bug-registry.md "queryKeys.ts namespaces omit orgId" GAP fix
    // (2026-08-06): added orgId -- see protocolCompliance.user's identical
    // comment above for the full reasoning (latent, not live-exploitable,
    // hardening pass).
    active: (orgId: string, userId: string) => ['negotiations', 'active', orgId, userId] as const,
    result: (negotiationId: number) => ['negotiations', 'result', negotiationId] as const,
  },
  // Spec 297 Phase B batch B12 (negotiations/TemplateBrowser.tsx, Spec 117 +
  // 119). GET /negotiation-templates -- distinct from `negotiations` above
  // (limitsAPI's active/result negotiation-session flow) and from
  // `negotiationsTab` below (page-level tab of that same flow) -- this is
  // the browsable TEMPLATE catalog, a different feature/endpoint entirely,
  // same word, different domain object (mirrors negotiationsTab's own
  // "same word, different feature" precedent framing).
  //
  // Literal prefix is 'negotiation-templates' (NOT folded into a differently-
  // named JS key) on purpose: negotiationTemplates.ts's own
  // createCustomTemplate/updateCustomTemplate already fire
  // `queryClient.invalidateQueries({queryKey:['negotiation-templates']})`
  // (pre-existing, previously-inert prefix -- same "becomes a real
  // invalidation target" pattern as chastity.all/protocols.all/points.all
  // above) -- TanStack's prefix-match invalidation only reaches this query if
  // the literal matches exactly, same constraint protocolTemplates.all's doc
  // comment documents.
  //
  // `list` folds in orgId even though GET /negotiation-templates takes no
  // orgId URL param -- the endpoint resolves the caller's ACTIVE org
  // server-side via `resolveActiveOrganizationId(req)` and returns org-custom
  // templates only when that context is present. TemplateBrowser.tsx is
  // rendered from NegotiationsTab.tsx (checklist/NegotiationsTab.tsx), a
  // persistent tab reachable while the header org-switch chip is used --
  // same "stays mounted across an org switch" reasoning as
  // `negotiationsTab.active`'s doc comment above, so folding orgId in here
  // gets the identical benefit (an org switch is structurally a fresh cache
  // entry via TanStack's own per-key caching, no ref-guard needed).
  //
  // NOT userId-scoped: confirmed via backend (negotiationRoutes.ts's GET / ->
  // negotiationTemplateService.getTemplates(orgId)) that the response is
  // system templates + this ORG's custom templates, identical for every
  // member who can browse -- genuinely org-shared, not "my own" data.
  negotiationTemplates: {
    all: () => ['negotiation-templates'] as const,
    list: (orgId: string | undefined) => ['negotiation-templates', 'list', orgId ?? null] as const,
  },
  // Spec 297 Phase B batch B12 (limits/LimitsReviewBanner.tsx, Spec 107).
  // Consent/limits-adjacent surface -- extra scrutiny applied, same category
  // as B1's safeword/useSafeword.ts and B3's negotiationsTab above.
  //
  // NOT userId-scoped: confirmed via backend
  // (limitReviewController.ts's own doc comment "Returns the current limits
  // review status. Any member can read" -> limitReviewService.getReviewStatus
  // (orgId), no per-caller filter) -- one shared review-due/overdue status
  // per organization, identical for every member. Genuinely org-shared, not
  // "my own account" data -- matches `protocols.lapseCandidates`'s
  // admin-only-but-org-shared precedent for the reasoning shape (here it's
  // not even admin-only to READ; only the mark-reviewed action is
  // Dominant-gated, enforced server-side, not by this query).
  limitsReview: {
    status: (orgId: string) => ['limits-review', orgId, 'status'] as const,
  },
  // Spec 312 — Negotiation Copilot pre-negotiation nudge. GET
  // /limits/checklist/unrated-flagged (feature-flag gated). Self-scoped to
  // the caller's own unrated user_limits rows (no partner data), but folded
  // in userId anyway per this file's own consent/limits-adjacent-surface
  // discipline (same reasoning as negotiationsTab.active's doc comment
  // above) -- an account switch in the same tab must not read a stale
  // cache entry belonging to the previous account.
  limitsChecklist: {
    unratedFlagged: (orgId: string, userId: string) => ['limits-checklist', orgId, userId, 'unrated-flagged'] as const,
  },
  // Spec 296 Package D. Prefix ('chastity') matches ChastityTabV2.tsx's own
  // pre-existing `queryClient.invalidateQueries({ queryKey: ['chastity'] })`
  // call (out of scope for this package).
  chastity: {
    // Bare (no orgId) -- matches the pre-existing exact
    // `queryKey: ['chastity']` invalidation call/assertion (ChastityTabV2.tsx,
    // useChastity.test.ts).
    all: () => ['chastity'] as const,
    dashboard: (orgId: string) => ['chastity', orgId, 'dashboard'] as const,
    history: (orgId: string, role: string, page: number) => ['chastity', orgId, 'history', role, page] as const,
    myRequests: (orgId: string) => ['chastity', orgId, 'my-requests'] as const,
    wearerDevices: (orgId: string, wearerId: string) => ['chastity', orgId, 'wearer-devices', wearerId] as const,
    // Spec 297 Phase A batch 2. GET /chastity/locks/:lockId/health-checks --
    // keyed by lockId (a lock's publicId), still a descendant of the bare
    // `['chastity']` prefix above so useChastity.ts's existing
    // `invalidateQueries({queryKey: ['chastity']})` on every chastity
    // mutation (including submitHealthMutation) reaches this too. Shared by
    // BOTH HealthCheckModal.tsx (check-in history list) and
    // LockDetailModal.tsx (timeline view) via one hook
    // (hooks/queries/useChastityHealthChecks.ts) -- see that file's doc
    // comment for why this replaced two independent fetches of the same
    // endpoint against what would otherwise have been the same cache key.
    healthChecks: (lockId: string) => ['chastity', lockId, 'health-checks'] as const,
    // Spec 297 Phase A batch 2 (HealthTrendChart.tsx). GET
    // /chastity/locks/:lockId/health-log?days=N -- days folded into the key
    // (mirrors discovery.feed's "every request-shaping input in the key"
    // precedent) so switching the 30d/60d/90d range is a fresh cache entry.
    healthLog: (lockId: string, days: number) => ['chastity', lockId, 'health-log', days] as const,
    // Spec 297 Phase A batch 7 (account-settings/HierarchySection.tsx). GET
    // .../keyholder-assignments/for-user/:userId?role=submissive -- "who is
    // MY keyholder" (the viewer, as a submissive). Deliberately NOT the same
    // key as useChastity.ts's dashboard composite query above, even though
    // both hit getKeyholderAssignmentsForUser: that one passes role='keyholder'
    // ("am I someone else's keyholder", bundled into ChastityDashboardData)
    // -- a different question, different role param, different response
    // meaning. Still prefixed with 'chastity' so the existing bare
    // `invalidateQueries({queryKey: ['chastity']})` convention (see `all`
    // above) reaches this too.
    keyholderAssignmentsForUser: (orgId: string, userId: string, role: 'submissive' | 'keyholder') =>
      ['chastity', orgId, 'keyholder-assignments-for-user', userId, role] as const,
  },
  // Spec 297 Phase A batch 2 (DomPendingRequests.tsx / MyRelationshipRequests.tsx,
  // Spec 235). Two distinct endpoints (Dom's inbox vs a Sub's own requests) --
  // separate keys, not one key with a view param, so approving/declining on
  // the Dom side never touches the Sub-side cache entry (and vice versa for
  // withdraw).
  relationshipRequests: {
    domPending: (orgId: string) => ['relationship-requests', orgId, 'pending'] as const,
    mine: (orgId: string) => ['relationship-requests', orgId, 'mine'] as const,
  },
  // Spec 296 Package D. Prefix ('scenes') matches useSceneLog.ts's own
  // pre-existing `queryClient.invalidateQueries({ queryKey: ['scenes'] })`
  // call. No orgId in the dashboard key -- listScenes/getSceneStats are
  // deliberately org-less-tolerant (userId-scoped, see useSceneLog.ts).
  sceneLog: {
    all: () => ['scenes'] as const,
    // orgId included even though listScenes/getSceneStats are org-less --
    // the bundled getChecklist() call IS org-scoped, so an org switch must
    // still produce a fresh cache entry (mirrors the pre-migration
    // loadData() useCallback's own `currentOrg` dependency).
    dashboard: (sceneTypeFilter: string, timePeriod: number, orgId: string) => ['scenes', 'dashboard', sceneTypeFilter, timePeriod, orgId] as const,
    shared: (orgId: string) => ['scenes', 'shared', orgId] as const,
    members: (orgId: string) => ['scenes', 'members', orgId] as const,
  },
  scenePlans: {
    all: () => ['scene-plans'] as const,
    // Spec 297 Phase A batch 4 (PlanPickerModal.tsx). Deliberately NOT
    // scenePlans.all() -- that key holds PlannerTab's composite
    // {plans, aftercareTemplates, allActivities} dashboard object (Spec 296
    // Package D), so reusing it here would have PlanPickerModal read that
    // shape as a bare plan array. `list()` is scenePlanAPI.list() alone as
    // its own cache entry -- still covered by
    // invalidateQueries({queryKey: scenePlans.all()})'s default prefix
    // match (['scene-plans'] matches ['scene-plans', 'list']), so a plan
    // create/update/delete/confirm via PlannerTab or ScenePlanConfirmFlow
    // still invalidates this too.
    //
    // SAFETY FIX (2026-08-04, Spec 297 Phase A post-merge fanout review,
    // MEDIUM -- design debt, not currently live-exploitable, see
    // `negotiations.active`'s SAFETY FIX comment above for the shared root
    // cause). Scoped by userId. GET /scene-plans is already org-scoped
    // server-side via requireOrganization (context-resolved, not a URL
    // param) -- unchanged and out of scope here.
    list: (userId: string) => ['scene-plans', 'list', userId] as const,
  },
  // Spec 297 Phase B batch B12 (scenes/LimitsWarningBanner.tsx, Spec 100).
  // GET /scene-plans/:id/limits-check -- deliberately its OWN top-level key,
  // NOT nested under the `['scene-plans']` prefix above: PlannerTab.tsx
  // already calls `invalidateQueries({queryKey: scenePlans.all()})` on every
  // plan create/update/delete AND separately, explicitly, bumps its own
  // `limitsRefreshKey` state specifically to re-trigger this banner's fetch
  // (see PlannerTab.tsx's handleSave) -- the app's own pre-existing design
  // already treats "plan list changed" and "re-check limits for this specific
  // plan" as two independent refresh triggers. Nesting under `scenePlans`'s
  // prefix would add an unrequested extra coupling (every plan mutation
  // anywhere would silently also invalidate every mounted limits-check),
  // duplicating the refreshKey mechanism instead of just using it -- same
  // "avoid unintended coupling, use a fully distinct top-level key" call as
  // `identity.registrationResume`'s doc comment above.
  //
  // `refreshKey` folds into the key itself (not driven by a manual
  // `refetch()` call): mirrors chastity.healthLog's/discovery.feed's "every
  // request-shaping input in the key" precedent, and reproduces the
  // pre-migration fetchLimitsCheck()'s exact loading behavior for free --
  // pre-migration called `setLoading(true)` at the top of EVERY fetch
  // including refreshKey-triggered ones, so "Checking limits..." reappeared
  // on each bump; a plain `refetch()` with refreshKey left out of the key
  // would leave `isLoading` false on a bump (only `isFetching` would flip),
  // silently dropping that loading text. Folding refreshKey in instead makes
  // each bump a genuinely NEW cache entry with its own fresh `isLoading`.
  // `refreshKey` is a small monotonically-incrementing int bumped only on
  // plan save (PlannerTab.tsx), so unbounded cache growth is not a practical
  // concern (mirrors chastity.healthLog's own days-in-key growth profile).
  //
  // NOT userId-scoped: confirmed via backend (scenePlanRoutes.ts's GET
  // /:id/limits-check -> scenePlanService.checkLimitsForPlan(planId, orgId))
  // that the result is a deterministic function of (planId, orgId) alone --
  // every Dominant who can view a given plan's limits cross-reference (route
  // is itself Dominant-gated via isEffectivelyDominantForUser) sees the
  // IDENTICAL warnings, not a per-viewer filtered subset. Genuinely
  // plan-shared data, not "my own account" data.
  //
  // NOT orgId-scoped either: a scene plan belongs to exactly one org, and
  // this banner unmounts/remounts with its parent plan view rather than
  // persisting across an org switch the way NegotiationsTab/TemplateBrowser
  // do (see `negotiationTemplates.list`'s doc comment above for that
  // contrasting case) -- planId alone already uniquely identifies the
  // correct cache entry.
  sceneLimitsCheck: {
    forPlan: (planId: string, refreshKey: number) => ['scene-limits-check', planId, refreshKey] as const,
  },
  // Spec 313 — Scene Composer. Deliberately its OWN top-level key, same
  // reasoning as sceneLimitsCheck immediately above: this is per-PAIR data
  // (dominantId/submissiveId), not per-plan, so nesting under scenePlans'
  // prefix would tie its cache lifetime to plan CRUD invalidation for no
  // reason. Catalog is keyed on the resolved pair (order-independent inputs,
  // but the route always resolves them in dominant/submissive order so the
  // key is naturally stable), discussState on the specific partner.
  sceneComposer: {
    catalog: (dominantId: string, submissiveId: string) => ['scene-composer', 'catalog', dominantId, submissiveId] as const,
    discussState: (partnerId: string) => ['scene-composer', 'discuss-state', partnerId] as const,
  },
  autonomy: {
    orgDetails: (orgId: string) => ['autonomy', orgId, 'org-details'] as const,
    // Spec 297 Phase B batch B5 (EarnedPrivilegesSection.tsx, Dominant view).
    // Bundles GET .../autonomy/privileges?status=active + GET
    // .../autonomy/rules into ONE query (mirrors sceneLog.dashboard/
    // collaring.data's precedent of "one query when pre-migration treats
    // multiple endpoint calls as one fetch-and-render unit") -- the
    // pre-migration fetchData() already fetched both via Promise.all under
    // one shared try/catch (a "not enabled" fallback clears BOTH lists to
    // []), and every mutation (grant/revoke/createRule/deleteRule) refetched
    // BOTH regardless of which one actually changed. NOT userId-scoped:
    // this is org-wide multi-submissive data visible identically to any
    // Dominant of this org (not "my own account" data, see queryKeys.ts's
    // userId-scoping rule) -- the response is a function of (orgId, caller
    // is-dominant), not of which specific Dominant is looking. Deliberately
    // a separate top-level key from StructureTabV2.tsx's pre-existing
    // `['structure-v2-autonomy', orgId]` (out of this batch's scope, found
    // while tracing this endpoint) -- that query passes a different filter
    // shape ({userId} for non-admins, no status) and returns privileges
    // only, no rules; a real future-consolidation candidate but not shared
    // here per the two-different-queryFn-shapes rule (see
    // organizations.membersForSelect's doc comment above).
    privilegesAndRules: (orgId: string) => ['autonomy', orgId, 'privileges-and-rules'] as const,
    // Spec 297 Phase B batch B5 (MyPrivilegesSection.tsx, Submissive's own
    // view). GET .../autonomy/privileges with no status/userId filter -- the
    // backend narrows non-admin callers to their own privileges regardless
    // of what's passed (backend/src/controllers/autonomyController.ts
    // getPrivileges: `effectiveUserId = isAdmin ? filterUserId : req.user.id`),
    // so this is genuinely "my own account" data.
    //
    // SAFETY: scoped by userId per queryKeys.ts's userId-scoping rule --
    // AuthContext's login()/register() don't clear the QueryClient, so a
    // bare key here could let one submissive's cached privileges bleed into
    // a different submissive's same-tab session after a non-logout account
    // switch (same root cause as `negotiations.active`'s SAFETY FIX comment
    // above). Deliberately its own key, not a reuse of `privilegesAndRules`
    // above (different filter -- no status, no rules bundled) or
    // StructureTabV2.tsx's `['structure-v2-autonomy', orgId]` (out of scope,
    // same caveat as `privilegesAndRules`'s doc comment).
    myPrivileges: (orgId: string, userId: string) => ['autonomy', orgId, 'my-privileges', userId] as const,
  },
  travel: {
    schedules: () => ['travel-schedules'] as const,
    // Spec 297 Phase B batch B4 (travel-schedule/useTravelSchedule.ts). GET
    // /travel-schedules is org-scoped (requireOrganization) -- the bare key
    // above is a previously-inert prefix already invalidated by
    // components/Travel/TravelScheduleModal.tsx's existing
    // `invalidateQueries({queryKey:['travel-schedules']})` call (out of this
    // batch's scope, unchanged). Adding orgId as a descendant (not changing
    // the bare key's shape) fixes a genuine correctness gap the pre-migration
    // hand-rolled state also had: switching Hierarchy via the header chip
    // left the previously-active Hierarchy's schedules rendered until the
    // next loadSchedules() call happened to fire, since the bare key alone
    // can't tell two orgs' data apart. TravelScheduleModal.tsx's bare-prefix
    // invalidation still reaches this descendant key under TanStack's
    // default prefix matching, so its cache-busting behavior is unaffected.
    forOrg: (orgId: string) => ['travel-schedules', orgId] as const,
    // Spec 297 Phase B batch B4. GET /travel-schedules/:id/inventory --
    // a schedule's own packing list, keyed by the schedule's id. Not a
    // descendant of `forOrg`'s prefix (different resource, different
    // endpoint) but still 'travel-schedules'-prefixed for the same
    // TravelScheduleModal.tsx bare-invalidation reach described above.
    // Reused as-is by components/Travel/TravelScheduleModal.tsx (Spec 297
    // Phase B batches B10+B11) -- confirmed identical queryFn
    // (travelScheduleAPI.getInventory(scheduleId) -> unwrapArray) and
    // `retry: false` at both call sites; only `enabled` gating differs
    // (this hook's `!!selectedSchedule` vs the modal's `!!schedule`), which
    // is safe multi-observer behavior, not a shape divergence.
    inventory: (scheduleId: number) => ['travel-schedules', 'inventory', scheduleId] as const,
    // Spec 297 Phase B batch B4. GET /inventory/for-tasks?ownerId=<userId> --
    // the caller's OWN inventory items available to add to a packing list.
    // Deliberately its own key, not a reuse of `inventory.forTask` above
    // (InventoryTableSelector.tsx): same endpoint/params shape when called
    // with a bare ownerId, but that component's queryFn also has an
    // `assigneesRef`-driven multi-assignee fallback path baked in and is
    // designed to be mounted only inside <InventoryTableSelector>, not
    // reused standalone -- see this file's membersForSelect/
    // verification.fullStatus precedent for why a differently-shaped queryFn
    // shouldn't share a cache key even when today's params happen to
    // coincide. Proactively scoped by userId (genuinely "my own inventory").
    // Reused as-is by components/Travel/TravelScheduleModal.tsx (Spec 297
    // Phase B batches B10+B11) -- confirmed identical queryFn
    // (inventoryAPI.getInventoryForTasks({ownerId}) -> unwrapResponse) and
    // `retry: false` at both call sites. `enabled` gating differs (this
    // hook's `addingItem && !!user` vs the modal's unconditional fetch) --
    // checked this isn't cosmetic: TravelTabV2.tsx's ActiveTripCard only
    // reads/renders `availableItems.map` inside its own `{addingItem ? ...}`
    // branch, and TravelScheduleModal only mounts when `showModal` is true,
    // so nothing in the page branches differently based on whether the
    // modal has already warmed this cache entry. The modal's eager fetch is
    // a harmless prefetch (faster "Add item" on a subsequent click), not a
    // behavior change to the page.
    availableItems: (userId: string) => ['travel-schedules', 'available-items', userId] as const,
    // Spec 297 Phase B batch B4. GET /organizations/:orgId/settings, read
    // only for `.settings.timezone`. Deliberately NOT a reuse of
    // queryKeys.organizations.settings(orgId) -- that key's own doc comment
    // (above, tasks.createDefaults' cross-reference) already documents TWO
    // incompatible queryFn shapes sharing it (a filed bug-registry GAP);
    // adding this hook's raw-envelope-then-`.settings` projection as a third
    // would deepen that exact footgun rather than fix it. Own key instead,
    // same reasoning as tasks.createDefaults' split.
    orgTimezone: (orgId: string) => ['travel-schedules', 'org-timezone', orgId] as const,
    // Date-range-picker busy-day marking (2026-08-09): GET /travel-schedules
    // filtered to a single target user's OWN trips, used only to shade
    // already-booked days on the calendar while picking new dates. Distinct
    // from `forOrg` (unfiltered, org-wide) -- this is per-user so switching
    // which member a Dominant is booking for doesn't show a stale user's
    // busy days. Still under the 'travel-schedules' prefix for
    // TravelScheduleModal's existing bare-invalidation reach.
    forUser: (userId: number) => ['travel-schedules', 'for-user', userId] as const,
    // Structural-debt backlog #8. GET .../travel-schedules/check-conflicts
    // per assignee, shared by pages/create-task/useCreateTask.ts and
    // pages/task-detail/useTaskEditState.ts's own travel-conflict checks --
    // both call travelScheduleAPI.checkConflicts(userId, start, end) with an
    // identical queryFn (raw hasConflicts/conflicts envelope, no per-file
    // enrichment baked in), so hooks/queries/useTravelConflicts.ts's
    // useTravelConflictsForUsers() is the single shared hook and this the
    // single shared key -- each caller does its own userName/date-field
    // enrichment on the raw result afterward, preserving each file's
    // pre-existing (and slightly different) mapping behavior.
    conflicts: (userId: string, startDate: string, endDate: string) =>
      ['travel-conflicts', userId, startDate, endDate] as const,
  },
  channels: {
    all: () => ['channels'] as const,
    detail: (channelId: string) => ['channels', channelId] as const,
    posts: (channelId: string | null, options?: { page?: number; sort?: string; category?: string }) =>
      ['channel-posts', channelId, options] as const,
    presence: (channelId: string) => ['channel-presence', channelId] as const,
    // Spec 292 Phase 5 (ChannelEventsFeed.tsx). Mirrors posts()'s shape
    // (same 'channels' namespace, same channelId-first scoping) deliberately —
    // ChannelFeed.tsx renders EITHER the posts query OR ChannelEventsFeed for
    // a given channel (never both: `isEventsChannel` gates posts() to
    // `enabled: false`), so the two never actually race for the same
    // channelId, but keeping the key shape and staleTime (30s) identical
    // means an events-channel view has the same invalidation/refetch cadence
    // a posts-channel view would, rather than inventing a second convention.
    events: (channelId: string | null, params?: { status?: 'upcoming' | 'past' | 'all' }) =>
      ['channel-events', channelId, params] as const,
    // Spec 299 Phase 1: the "jump to post" window fetch. Keyed on
    // channelId+postId+commentId — a different target (or same post
    // re-landed-on via a different notification with a different
    // highlighted comment) is genuinely a different window, not a refetch
    // of the same one.
    postsAround: (channelId: string | null, postId: string | null, commentId?: string | null) =>
      ['channel-posts-around', channelId, postId, commentId] as const,
    // Structural-debt backlog #8 (components/channels/EventWorkshops.tsx).
    // GET /channels/:channelId/events/:eventId/workshops.
    eventWorkshops: (channelId: string | undefined, eventId: number) =>
      ['channel-event-workshops', channelId, eventId] as const,
    // Structural-debt backlog #8 (components/channels/CreateChannelDialog.tsx
    // edit mode). Members/pending tabs, fetched only once activated.
    members: (channelId: string) => ['channel-members', channelId] as const,
    pendingRequests: (channelId: string) => ['channel-pending-requests', channelId] as const,
  },
  channelChallenges: {
    // Shared root so mutations can invalidate every list (all status filters)
    // for a channel in one call: ['channel-challenges', channelId, 'list'].
    list: (channelId: string, status?: string) => ['channel-challenges', channelId, 'list', status] as const,
    listRoot: (channelId: string) => ['channel-challenges', channelId, 'list'] as const,
    // Cheap existence check driving the Feed/Challenges tab's visibility.
    hasAny: (channelId: string) => ['channel-challenges', channelId, 'has-any'] as const,
    detail: (channelId: string, challengeId: string) => ['channel-challenges', channelId, 'detail', challengeId] as const,
    leaderboard: (channelId: string, challengeId: string) => ['channel-challenges', channelId, 'leaderboard', challengeId] as const,
  },
  channelPolls: {
    // Shared root so the vote/create mutations can invalidate the whole list
    // in one call — mirrors channelChallenges.listRoot above.
    list: (channelId: string) => ['channel-polls', channelId, 'list'] as const,
    detail: (channelId: string, pollId: string) => ['channel-polls', channelId, 'detail', pollId] as const,
  },
  channelModeration: {
    // Moderator-only full log — action-type filter folded into the key so
    // switching filter pills gets its own cache entry.
    log: (channelId: string, actionType?: string) => ['channel-moderation', channelId, 'log', actionType] as const,
    // CEO visibility split: the caller's own notices, never another
    // member's — no target-user parameter, so there is nothing here that
    // could be pointed at someone else's cache entry.
    mine: (channelId: string) => ['channel-moderation', channelId, 'mine'] as const,
  },
  channelTags: {
    // Categories enabled for a channel + curated values (member view).
    enabledCategories: (channelId: string) => ['channel-tags', channelId, 'enabled-categories'] as const,
    // Caller's own values in this channel.
    mine: (channelId: string) => ['channel-tags', channelId, 'mine'] as const,
    // Bulk read for post-author byline rendering — scoped to the caller-
    // supplied set of visible author ids, not just channelId, so distinct id
    // sets don't collide on one cache entry. Stable serialization
    // (sorted-and-joined) so key identity doesn't depend on array order.
    members: (channelId: string, userPublicIds: string[]) =>
      ['channel-tags', channelId, 'members', [...userPublicIds].sort().join(',')] as const,
    // Prefix for invalidating every members() variant for a channel at once
    // (TanStack Query does partial/prefix matching by default).
    membersAll: (channelId: string) => ['channel-tags', channelId, 'members'] as const,
    // Moderator: catalog categories not yet enabled here.
    availableCategories: (channelId: string) => ['channel-tags', channelId, 'available-categories'] as const,
    // Channels admin: pending category-request queue (not channel-scoped).
    pendingRequests: () => ['channel-tags', 'pending-requests'] as const,
  },
  search: {
    full: (query: string, scope?: string, limit?: number, offset?: number) =>
      ['search', query, scope, limit, offset] as const,
    quick: (query: string, limit?: number) =>
      ['search-quick', query, limit] as const,
  },
  journal: {
    entries: (params?: Record<string, unknown>) => ['journal', 'entries', params] as const,
    entry: (entryId: string) => ['journal', 'entry', entryId] as const,
    entryByDate: (date: string) => ['journal', 'entry-by-date', date] as const,
    calendar: (year: number, month: number) => ['journal', 'calendar', year, month] as const,
    tags: () => ['journal', 'tags'] as const,
    moodTrends: (days?: number, filter?: string) => ['journal', 'mood-trends', days, filter] as const,
    moodStats: (days?: number, filter?: string) => ['journal', 'mood-stats', days, filter] as const,
    todaysMood: () => ['journal', 'mood-today'] as const,
    sharedMoods: (days?: number) => ['journal', 'shared-moods', days] as const,
    scenes: (params?: Record<string, unknown>) => ['journal', 'scenes', params] as const,
    // Structural-debt backlog #8 (pages/journal/useJournal.ts). GET
    // /journal/entries/shared-with-me. Mirrors `entries` above's shape but a
    // different endpoint entirely -- own key. orgId included since
    // journalAPI.getEntriesSharedWithMe takes an explicit orgId param
    // (unlike `entries`, which is org-less-tolerant per
    // backend/src/controllers/journalController.ts's listEntries comment).
    sharedEntries: (orgId: string | undefined, params?: Record<string, unknown>) =>
      ['journal', 'shared-entries', orgId, params] as const,
    // checkinAPI.getHistory(daysToFetch), read only for the calendar's
    // mood-overlay -- org-scoped (requireOrganization on checkinRoutes,
    // unlike the org-less-tolerant journal endpoints above). Keyed by
    // year+month (not daysToFetch, which is a derived implementation detail
    // of "this month plus days-since-month-end") since that's the actual
    // semantic identity of what's being displayed.
    checkinMoodsForCalendar: (orgId: string, year: number, month: number) =>
      ['journal', 'checkin-moods-calendar', orgId, year, month] as const,
    // Structural-debt backlog #8 (pages/journal/JournalNewPage.tsx's media
    // picker). GET /media?limit=50, no filters -- a flat, unfiltered "recent
    // media to attach" list. Deliberately NOT a reuse of
    // queryKeys.mediaLibrary.items(userId, filters) -- that key's queryFn
    // shape includes filterType/tags/search/page filtering this call never
    // sends, and is owned by the full Media Library page's own pagination
    // state, not this lightweight picker.
    newEntryMedia: () => ['journal', 'new-entry-media'] as const,
    // Structural-debt backlog #8 (pages/journal/JournalNewPage.tsx's task
    // picker). GET /organizations/:orgId/tasks?search=... for linking tasks
    // to a new entry -- own key (different params shape than any existing
    // task-list query in the app; a debounced free-text search of ALL org
    // tasks, not the paginated/sorted/status-filtered main task list).
    newEntryTasks: (orgId: string | undefined, search: string) =>
      ['journal', 'new-entry-tasks', orgId, search] as const,
  },
  training: {
    programs: (params?: Record<string, unknown>) => ['training', 'programs', params] as const,
    program: (programId: number) => ['training', 'program', programId] as const,
    enrollments: (params?: Record<string, unknown>) => ['training', 'enrollments', params] as const,
    enrollmentProgress: (enrollmentId: number) => ['training', 'enrollment-progress', enrollmentId] as const,
    certificates: () => ['training', 'certificates'] as const,
    // Spec 297 Phase A batch 5. useTrainingPrograms.ts's 'review-queue' tab
    // (Dom review of submitted tasks) -- org-scoped like `programs` above.
    //
    // SAFETY FIX (2026-08-04, Spec 297 Phase A post-merge fanout review,
    // MEDIUM -- design debt, not currently live-exploitable, see
    // `negotiations.active`'s SAFETY FIX comment above for the shared root
    // cause). Scoped by userId. orgId ALSO added 2026-08-06 (bug-registry.md
    // "queryKeys.ts namespaces omit orgId" GAP fix -- see
    // protocolCompliance.user's comment above for the full reasoning).
    reviewQueue: (orgId: string, userId: string) => ['training', 'review-queue', orgId, userId] as const,
  },
  notifications: {
    all: (unreadOnly?: boolean) => ['notifications', { unreadOnly }] as const,
    archived: () => ['notifications', 'archived'] as const,
    preferences: () => ['notifications', 'preferences'] as const,
  },
  // Structural-debt backlog #8 follow-up sweep (pages/support/useSupport.ts).
  // Global, static support content -- no user/org scoping (identical for
  // every caller), fetched only when the FAQ/changelog tab is opened.
  support: {
    faqs: () => ['support', 'faqs'] as const,
    changelog: () => ['support', 'changelog'] as const,
  },
  featureLimits: {
    all: ['feature-limits'] as const,
    status: () => ['feature-limits', 'status'] as const,
  },
  // Structural-debt backlog #8 (pages/feature-flags/useFeatureFlags.ts).
  // Global platform-admin resource (super_admin-only, not per-user data) --
  // no userId scoping needed, unlike the "my own data" keys elsewhere in
  // this file.
  adminFeatureFlags: {
    all: ['admin-feature-flags'] as const,
    history: (flagName: string) => ['admin-feature-flags', 'history', flagName] as const,
  },
  setupChecklist: {
    all: (orgId: string) => ['setup-checklist', orgId] as const,
  },
  dropDetection: {
    events: (orgId: string) => ['dropDetection', 'events', orgId] as const,
    history: (orgId: string, limit?: number) => ['dropDetection', 'history', orgId, limit] as const,
    event: (eventId: string) => ['dropDetection', 'event', eventId] as const,
  },
  milestones: {
    all: (orgId: string) => ['milestones', orgId] as const,
  },
  aftercare: {
    templates: (orgId: string) => ['aftercare', 'templates', orgId] as const,
    checklist: (orgId: string) => ['aftercare', 'checklist', orgId] as const,
    session: (sessionId: number) => ['aftercare', 'session', sessionId] as const,
    byScene: (sceneEntryId: string) => ['aftercare', 'byScene', sceneEntryId] as const,
  },
  checkins: {
    partner: (userId: string, days?: number) => ['checkins', 'partner', userId, days] as const,
    // Spec 296 Package E — ChronicleTodayPage's OWN check-in (not the partner's).
    today: () => ['checkins', 'today'] as const,
    trends: (days?: number) => ['checkins', 'trends', days] as const,
    stats: (days?: number) => ['checkins', 'stats', days] as const,
  },
  // Spec 297 Phase A batch 3 — Spec 111 Emergency Check-In Timer (safety
  // dead-man's switch), NOT the same domain as `checkins` above (Spec 296
  // relationship/wellness check-ins). Deliberately namespaced separately —
  // same word, different feature, different endpoint (`safetyCheckinAPI`).
  //
  // Note: `useFABContext.ts` already runs its own useQuery against the same
  // `safetyCheckinAPI.getActive()` endpoint under a different key
  // (['fab-context', 'safety-timer', orgId]), with a reduced derived shape
  // (active-or-null), a 30s staleTime, and retry:1 — appropriate for a
  // peripheral FAB indicator but not for this page's full-object,
  // always-fresh (retry:false) safety-critical need. Left as a separate,
  // pre-existing call rather than forced into a shared hook: unifying them
  // would either weaken this page's freshness/retry guarantees or change
  // the FAB's existing 30s-stale behavior, both out of scope for a
  // behavior-invisible migration.
  //
  // Skeptical-review MEDIUM (2026-08-04): the FAB's own cache could still
  // show a stale safety-timer state for up to 30s after start/confirm/cancel
  // on THIS page (two independent cache entries, no shared invalidation).
  // Fixed by having SafetyCheckinPage.tsx's three mutations also
  // `invalidateQueries({ queryKey: ['fab-context', 'safety-timer'] })` (a
  // prefix match — deliberately omits `orgId` so it invalidates regardless
  // of which org's FAB entry is cached) on success, alongside their own
  // `setQueryData` write to this namespace.
  //
  // Spec 297 Phase B batch B6: components/helm/SafetyTimerDashboardWidget.tsx
  // (the dashboard's compact quick-start/confirm widget) now ALSO reads/
  // writes `active(userId)` below — confirmed identical queryFn (same
  // safetyCheckinAPI.getActive() call, same unwrapResponse shape, same
  // `retry: false`) to SafetyCheckinPage.tsx's, so it's a safe reuse under
  // this file's "two queryFn bodies must never share a key" rule. This is a
  // genuine, deliberate THIRD consumer of the same underlying resource
  // (alongside the FAB's own reduced-shape cache above and this page) —
  // deliberately still NOT wired to invalidate the FAB's
  // ['fab-context', 'safety-timer'] entry from the widget's own mutations
  // (unlike the page above): doing so would be a real behavior improvement
  // beyond this migration's pure data-fetching-swap scope (see sprint
  // contract § 2 "Out of scope" — no consumer-facing behavior changes).
  // Filed as the same pre-existing staleness gap this page's own comment
  // already documents, just now with one more mutator; not fixed here.
  safetyCheckin: {
    // SAFETY FIX (2026-08-04, Spec 297 Phase A post-merge fanout review,
    // MEDIUM -- design debt, not currently live-exploitable, see
    // `negotiations.active`'s SAFETY FIX comment above for the shared root
    // cause). This is a genuine safety-critical surface (the dead-man's-
    // switch emergency timer) -- scoped by userId so it can never bleed
    // across a same-tab account switch. `status(checkinId)` below is a
    // DIFFERENT query (the partner-facing status link) and is deliberately
    // UNCHANGED.
    active: (userId: string) => ['safety-checkin', 'active', userId] as const,
    status: (checkinId: string) => ['safety-checkin', 'status', checkinId] as const,
    // Spec 297 Phase B batch B5 (SafetyHubWidget.tsx's expanded Quick
    // Actions panel). Same safetyCheckinAPI.getActive() endpoint as `active`
    // above, but deliberately its OWN key, not a reuse: this widget polls
    // every 60s ONLY while its details panel is expanded AND the
    // emergencyTimer feature is on (via `refetchInterval`), a materially
    // different per-query option set than `active`'s page-level always-
    // fresh/no-poll/retry:false contract (see SafetyCheckinPage.tsx's doc
    // comment) -- sharing the key would mean whichever mounts first
    // silently wins the options for both (see organizations.
    // membersForSelect's doc comment above for this exact footgun). Still
    // userId-scoped for the same safety-critical reason as `active` above.
    widgetPoll: (userId: string) => ['safety-checkin-widget-poll', userId] as const,
  },
  // Spec 314 — Check-in Buddy Network. GET /api/safety/buddies returns both
  // directions (designated by the caller AND designating the caller) in one
  // list, so a single userId-scoped key covers every consumer (buddy list
  // page, the check-in-start "who gets notified" info block, and the
  // designate-flow's already-a-buddy/already-pending exclusion check) --
  // deliberately one shared cache entry, not split by consumer, since
  // they're all reading the exact same resource with no per-consumer option
  // differences (unlike safetyCheckin.active vs .widgetPoll above).
  checkinBuddies: {
    list: (userId: string) => ['checkin-buddies', 'list', userId] as const,
  },
  // Spec 297 Phase B batch B5 (SafetyHubWidget.tsx). GET /safety-hub/summary
  // -- explicitly documented in that component's own header comment as
  // "Shows only the authenticated user's own safety configuration", no
  // orgId param at all. Scoped by userId per queryKeys.ts's userId-scoping
  // rule (same root cause as `negotiations.active`'s SAFETY FIX comment
  // above) -- this is a safety-critical surface (safeword/limits/
  // verification status), so a same-tab account switch leaking a prior
  // user's safety config here would be worse than most.
  safetyHub: {
    summary: (userId: string) => ['safety-hub-summary', userId] as const,
  },
  account: {
    // Spec 296 Package A: shared across every mounted probe consumer
    // (NavQuickSwitch + AccountSwitcher) so they observe ONE cache entry
    // instead of hand-rolled module-singleton state.
    probeStatuses: () => ['account-probe-statuses'] as const,
  },
  siteBanner: {
    active: () => ['site-banner-active'] as const,
  },
  partnerInvite: {
    reminderStatus: (organizationId: string) => ['partner-invite-reminder-status', organizationId] as const,
  },
  inlineInvite: {
    prompt: (organizationId: string, context: string) => ['inline-invite-prompt', organizationId, context] as const,
  },
  auth: {
    // Keyed by userId so an account switch never serves a stale prior
    // user's cached ToS status (mirrors organizations.settings(orgId)).
    tosStatus: (userId: string) => ['tos-status', userId] as const,
    // GET /auth/reset-password/validate/:token (ResetPasswordPage.tsx). A
    // reset token is single-use/pre-auth — no userId exists yet, keyed by
    // the token itself (unique per request, no cross-viewer cache concern).
    resetTokenValidation: (token: string) => ['auth', 'reset-token-validation', token] as const,
    // Spec 296 Checkpoint 3 (AuthContext.tsx). Deliberately UNSCOPED (no
    // userId param), unlike tosStatus above -- two of this key's three call
    // sites (loginWithToken, the impersonation bootstrap) fetch the profile
    // BEFORE the userId is known; that's the whole point of the call. Safety
    // against cross-user bleed comes from staleTime: 0 on every fetchQuery()
    // call against this key plus the existing queryClient.clear() in
    // AuthContext's logout()/handleAuthError(), not from key scoping -- see
    // AuthContext.tsx for the full reasoning.
    profile: () => ['auth-profile'] as const,
    // Spec 297 Phase A batch 8 (useRegister.ts's debounced username-
    // availability check). GET /auth/check-username?username=... -- the
    // debounced candidate username folds into the key itself (mirrors
    // discovery.feed's "every request-shaping input in the key" precedent)
    // so each distinct candidate is its own cache entry; the 400ms debounce
    // (unchanged from pre-migration) still gates WHEN the query fires via
    // `enabled`, this key just decides what gets cached once it does.
    usernameAvailability: (username: string) => ['auth-username-availability', username] as const,
  },
  push: {
    // Browser-capability + subscription-state probe (VAPID/FCM support,
    // Notification.permission, existing subscription) -- not server-list
    // data, so no params; see PushNotificationContext.tsx.
    status: () => ['push-notification-status'] as const,
  },
  // Spec 296 Checkpoint 1 (FeaturesContext.tsx). Bundles feature flags +
  // tierGate + per-pillar tab order in one key -- mirrors the pre-migration
  // Promise.all (see that file's loadFeaturesData() docstring for why this
  // stays one query rather than splitting). orgId (not bare, unlike
  // push.status() above) because this data genuinely varies per
  // organization; null means "no org selected" (getFeatures is still called,
  // with orgId undefined, in that case -- see FeaturesContext.tsx).
  //
  // ['features', orgId] is a PREFIX of the bare ['features'] key already
  // used (pre-dating this migration) by several admin-panel invalidation
  // calls -- organization-settings/FeaturesTab.tsx, feature-flags/
  // useFeatureFlags.ts, platform-config/usePlatformConfig.ts. Those calls
  // were previously inert (nothing was ever cached under '['features']'
  // before this migration); TanStack's default partial-key matching now
  // makes them real, so an admin toggling an org's feature overrides or a
  // super_admin editing a platform feature flag will invalidate this query
  // too, same intentional-prefix pattern as points/protocols/chastity/
  // orgasmBank above.
  features: {
    all: (orgId: string | null) => ['features', orgId] as const,
  },
  subCommandPresence: {
    protocols: (orgId: string) => ['sub-command-presence', 'protocols', orgId] as const,
    chastity: (orgId: string) => ['sub-command-presence', 'chastity', orgId] as const,
    travel: (orgId: string) => ['sub-command-presence', 'travel', orgId] as const,
    reviews: (orgId: string) => ['sub-command-presence', 'reviews', orgId] as const,
    economy: (orgId: string) => ['sub-command-presence', 'economy', orgId] as const,
  },
  // Spec 292 Phase 1 — DM conversation list + thread messages. Both
  // ConversationList.tsx and MessageThread.tsx read/write `conversations()`
  // (unread counts, mute state, last-message preview) so the sidebar and an
  // open thread can't drift out of sync waiting on separate poll cycles.
  messages: {
    conversations: () => ['messages', 'conversations'] as const,
    thread: (conversationId: string) => ['messages', 'thread', conversationId] as const,
    // Spec 297 Phase B batches B10+B11 (NewConversationModal.tsx). GET
    // /messages/users/search?q= -- confirmed via messagingService.searchUsers:
    // excludes the CALLER themselves (`u.id != currentUserId`) and anyone
    // blocked in either direction with the caller, so the result set
    // genuinely varies by viewer, not just by search text. Scoped by BOTH
    // query text (every request-shaping input, mirrors discovery.feed's
    // precedent) and userId (mandatory per this batch's discipline --
    // AuthContext's login()/register() don't queryClient.clear(), so a
    // same-tab account switch could otherwise resurface a prior user's
    // cached search results, including people the NEW user has blocked or
    // who have blocked the new user).
    userSearch: (query: string, userId: string) => ['messages', 'user-search', query, userId] as const,
    // Scoped by userId, same discipline as userSearch above — AuthContext's
    // login()/register() don't queryClient.clear(), so an unscoped key could
    // show the PREVIOUS user's unread DM badge to a newly-logged-in user
    // until the next poll cycle.
    unreadCount: (userId: string) => ['messages', 'unread-count', userId] as const,
    // GET /messages/blocked-users (BlockedUsersSection.tsx, account
    // settings). userId-scoped — "my own" account state, same discipline as
    // userSearch/unreadCount above. Distinct from discoveryBlockedUsers
    // below (a different endpoint/UI surface, /discovery/blocked).
    blockedUsers: (userId: string) => ['messages', 'blocked-users', userId] as const,
  },
  // Spec 292 Phase 2 — Discovery feed (CultivateFeed.tsx). `feed()` folds
  // filters/sortMode/location into the key itself (mirrors channelChallenges'
  // list/listRoot shape below) so changing any of them is structurally a new
  // cache entry — TanStack fetches fresh rather than depending on a useEffect
  // deps array remembering to include the new value (see Spec 290's H7
  // stale-closure bug this replaces). `feedRoot()` lets the like/pass
  // mutations splice a profile out of EVERY cached filter/sort variant, not
  // just the currently-active one, so flipping sort/filters back doesn't
  // resurface someone already liked/passed in this session.
  discovery: {
    feedRoot: () => ['discovery', 'feed'] as const,
    feed: (
      filters: Record<string, unknown>,
      sortMode: string,
      location: { lat: number; lng: number } | null,
    ) => ['discovery', 'feed', filters, sortMode, location] as const,
  },
  // Spec 292 Phase 3 — Discovery match chat (CultivateChat.tsx). A separate
  // namespace from `messages` above (DM) even though the shapes rhyme —
  // different backend (discoveryMessagingRoutes vs messagesRoutes), different
  // domain, and CultivateMessages.tsx (Discovery's conversation-list page) is
  // NOT part of this migration phase, so `conversations()` here is read only
  // by this one chat page (to resolve the partner's name/photo/id — there is
  // no single-conversation GET endpoint) rather than shared sidebar state the
  // way DM's `messages.conversations()` is.
  discoveryMessages: {
    conversations: () => ['discovery-messages', 'conversations'] as const,
    thread: (conversationId: string) => ['discovery-messages', 'thread', conversationId] as const,
    // Separate key from `thread` — see useDiscoveryMessagesFallbackPoll's doc
    // for why the disconnected-socket fallback poll is its own query rather
    // than an automatic refetch of the `thread` infinite query.
    fallbackPoll: (conversationId: string) => ['discovery-messages', 'fallback-poll', conversationId] as const,
  },
  // Spec 292 Phase 4 — LDR partner presence bar (PartnerPresenceBar.tsx).
  ldr: {
    partnerPresence: (orgId: string) => ['ldr', 'partner-presence', orgId] as const,
    // Spec 297 Phase A batch 7 (account-settings/ProfileSection.tsx's
    // TimezoneSelector). GET/PUT /users/me/timezone -- lives in ldrAPI
    // (services/api/ldr.ts) even though the route itself is a plain users
    // endpoint, not LDR-specific; keyed under this namespace to match where
    // the client-side API call lives.
    //
    // SAFETY FIX (2026-08-04, Spec 297 Phase A post-merge fanout review,
    // MEDIUM -- design debt, not currently live-exploitable, see
    // `negotiations.active`'s SAFETY FIX comment above for the shared root
    // cause). Originally bare -- now scoped by userId.
    timezone: (userId: string) => ['ldr', 'timezone', userId] as const,
  },
  // Spec 296 Package E — shared by ShareModal.tsx (LegacyShareModal branch) and
  // ShareSheet.tsx (both reachable behind the unifiedSharing kill-switch, see
  // hooks/queries/useSharing.ts's file doc). Keyed by entityType+entityId since a
  // single entity's shares are never relevant across entity types.
  sharing: {
    shares: (entityType: string, entityId: string) => ['sharing', entityType, entityId] as const,
  },
  contracts: {
    list: (orgId: string) => ['contracts', orgId] as const,
    // Unscoped prefix — matches this file's pre-migration invalidate shape
    // exactly (invalidates every org's cached list in one call).
    all: ['contracts'] as const,
  },
  // Spec 294 category 3 (useTerminology.ts). orgId (not bare) since the
  // fetched settings genuinely vary per org; null means "no org selected"
  // (query is `enabled: false` in that case -- see useTerminology.ts).
  terminology: {
    forOrg: (orgId: string | null) => ['terminology', orgId] as const,
    // Spec 297 Phase A batch 6 (TerminologySection.tsx, the settings-page
    // editor). Deliberately NOT `forOrg` above, even though both read the
    // same organizationAPI.getSettings(orgId)'s `.settings.terminology`
    // field -- `forOrg`'s queryFn (useTerminology.ts's fetchTerminology)
    // deliberately SWALLOWS fetch errors to `{}` with only a console.error
    // (silent fallback-to-defaults, correct for the ~69 read-only label
    // consumers spread across the app). This editor page's pre-migration
    // behavior surfaces a `showError` toast on failure instead -- a real,
    // deliberate UX difference for the one page whose job is editing this
    // data. Two different queryFn bodies can't safely share one cache key
    // (TanStack keeps exactly one queryFn/options registration per key --
    // see `organizations.membersForSelect`'s doc comment for the same
    // footgun), so this gets its own key + its own queryFn rather than
    // reusing `forOrg`'s. Not a new duplicate fetch: pre-migration this page
    // already issued its own independent GET /settings, separate from
    // DynamicTab's own `useTerminology()` call for `t()` label lookups on
    // the very same page -- unchanged by this migration.
    forSettingsPage: (orgId: string) => ['terminology-settings-page', orgId] as const,
  },
  // Spec 294 category 3 (useCollaring.ts). Single combined query -- mirrors
  // sceneLog.dashboard's precedent of bundling multiple endpoint calls
  // (current stages + history + members, via Promise.allSettled) into ONE
  // query since the pre-migration loadData() already treats them as one
  // fetch-and-render unit.
  //
  // SAFETY FIX (bug-registry.md "collaring.data 6th cross-user cache-leak
  // instance" GAP, closed 2026-08-06): was org-scoped only. The bundled
  // fetchCollaringData's `current` slice hits the SAME
  // getCurrentStageForOrg(orgId, userId)-filtered endpoint as `stages.current`
  // above (which IS correctly userId-scoped) -- confirmed via backend that
  // the response genuinely varies by VIEWER within the same org. Without
  // userId here, a same-tab account switch could render a prior viewer's
  // stage-pairing set to the new session on CollaringPage.tsx. `history`
  // and `members` are bundled into this SAME query/key (fetchCollaringData
  // treats all three as one atomic fetch unit, unlike `stages.current`/
  // `.history`'s deliberately separate keys) -- adding userId to the whole
  // key is correct even though history itself is org-shared, admin-gated
  // data, because this key covers the bundle, not history alone.
  collaring: {
    data: (orgId: string, userId: string) => ['collaring', orgId, userId] as const,
    // Prefix form, for invalidating every viewer's cache entry for an org at
    // once (mutations that change shared underlying data, not just the
    // mutating user's own view) -- see useCollaring.ts's addReflection/
    // deleteReflection and StagesSection.tsx's revert/advance invalidation
    // for the 4 call sites this replaces (skeptical-review LOW, 2026-08-06:
    // those sites previously hardcoded the raw literal ['collaring', orgId]
    // directly, risking silent drift if this key's shape ever changes).
    dataPrefix: (orgId: string) => ['collaring', orgId] as const,
  },
  // Spec 297 Phase B batch B12 (command/StagesSection.tsx, Spec 108). Same
  // underlying dynamic_stages table/endpoints as `collaring.data` above
  // (CollaringPage.tsx, Chronicle pillar) -- CONSIDERED reusing that key,
  // rejected: collaring.data's queryFn always fetches current+history+members
  // together via Promise.allSettled (bundled unit), while this component
  // fetches history LAZILY (only after an admin clicks "Show History", its
  // own showHistory-gated effect) and never fetches members at all (received
  // as a prop from the parent settings page). Two independently-maintained
  // queryFn shapes sharing one key is the exact footgun
  // organizations.membersForSelect's doc comment documents -- separate keys.
  // Also deliberately NOT nested as a descendant of `['collaring', orgId]`:
  // that would mean a revert here silently also invalidates/couples with
  // CollaringPage's cache, a new coupling neither component asked for.
  //
  // SAFETY: `current` is userId-scoped. Confirmed via backend
  // (dynamicStageRoutes.ts's GET /current -> dynamicStageService.ts's
  // getCurrentStageForOrg(orgId, userId)) that the response genuinely varies
  // by VIEWER within the same org -- the SQL adds
  // `WHERE ... AND (ds.dominant_id = $2 OR ds.submissive_id = $2)` whenever a
  // userId is passed, and the route ALWAYS passes req.user.id unconditionally
  // (no admin bypass): a Dominant naturally sees every pair where they're the
  // dominant party (their own id repeats across every submissive's stage
  // row), while a submissive sees only their own pairing. Without userId in
  // the key, a same-tab account switch (AuthContext.tsx's login()/register()
  // don't queryClient.clear()) could render a PRIOR viewer's stage-pairing
  // set to the new session -- same root cause as every other userId-scoped
  // SAFETY FIX in this file (see `negotiations.active`'s comment above).
  //
  // `collaring.data(orgId, userId)` above hits this SAME
  // getCurrentStageForOrg(orgId, userId)-filtered endpoint -- was NOT
  // userId-scoped when this batch shipped (filed as a GAP), closed
  // 2026-08-06, see that key's own comment for the fix.
  //
  // `history` is NOT userId-scoped: GET /history (requireAdmin) is called at
  // this route with only orgId (no dominantId/submissiveId filter args), so
  // it returns the full org history identically to every admin who can reach
  // it -- genuinely org-shared, admin-gated data (same reasoning as
  // `protocols.lapseCandidates`'s admin-only-but-org-shared precedent above),
  // not the userId-scoped SAFETY FIX pattern.
  stages: {
    current: (orgId: string, userId: string) => ['stages', orgId, 'current', userId] as const,
    history: (orgId: string) => ['stages', orgId, 'history'] as const,
  },
  // Spec 294 category 3 (useActiveRole.ts). Keyed by orgId+userId -- the
  // GET .../members/:userId/dynamic-role endpoint is per-member, not just
  // per-org.
  activeRole: {
    dynamicRole: (orgId: string, userId: string) => ['active-role', orgId, userId] as const,
  },
  // Spec 297 Phase A batch 7 (account-settings/HierarchySection.tsx). Same
  // organizationAPI.getDetails(orgId) endpoint as organizations.settings
  // above, but deliberately its OWN key, not a reuse -- organizations.settings
  // used to have multiple incompatible queryFn shapes sharing it (fixed
  // 2026-09-07, bug-registry 0zzz-bd, by splitting them onto nested keys),
  // and this component's own fetch (full raw details: members + subscription)
  // would have been a further one. The separation stands on its own merits. Mirrors
  // autonomy.orgDetails' precedent of "same endpoint, own dedicated key."
  // ONE key serves both this component's uses: the mount-time query (gated
  // on isSub && subInitiationRequests, reads .members for the sub-request
  // dominant picker) and the Leave-Hierarchy click handler's on-demand
  // `queryClient.fetchQuery({ ..., staleTime: 0 })` call (reads .members +
  // .subscription) -- both share the identical fetch/unwrap function, see
  // HierarchySection.tsx.
  hierarchySectionOrgDetails: {
    full: (orgId: string) => ['hierarchy-section-org-details', orgId] as const,
  },
  // Structural-debt backlog #8 (organization-settings/useSettingsCore.ts).
  // A THIRD caller of organizationAPI.getDetails(orgId), same precedent as
  // hierarchySectionOrgDetails above -- own dedicated key rather than
  // reusing organizations.settings (different endpoint entirely) or
  // hierarchySectionOrgDetails (different queryFn: this one reads
  // .organization.name/.userRole/.members, not .members/.subscription).
  settingsCoreOrgDetails: {
    full: (orgId: string) => ['settings-core-org-details', orgId] as const,
  },
  // Same file's GET /organizations/:id/join-requests?status=pending fetch.
  settingsCoreJoinRequests: {
    pending: (orgId: string) => ['settings-core-join-requests', orgId, 'pending'] as const,
  },
  // Spec 294 category 2 (mutation-then-manual-refetch trio). Admin
  // content-report queue (ModerationQueuePage.tsx) — GET /admin/reports,
  // requireAdmin-gated. Distinct from `messageReports` below: different
  // table (content_reports vs message_reports), different gate
  // (requireAdmin vs requireSuperAdmin).
  moderationReports: {
    // Bare prefix — resolving a report changes both the list AND the stats
    // card counts (pending/critical/resolvedToday), so the resolve
    // mutation invalidates this one prefix to refresh both in one call,
    // matching pre-migration's `loadReports(); loadStats();` pairing.
    all: () => ['moderation-reports'] as const,
    // Filters folded into the key (status/priority/category) so changing
    // any of them is structurally a new cache entry, matching pre-migration
    // loadReports()'s useCallback deps.
    list: (filters: { status: string; priority: string; category: string }) =>
      ['moderation-reports', 'list', filters] as const,
    stats: () => ['moderation-reports', 'stats'] as const,
  },
  // Spec 294 category 2. DM message-report queue (AdminMessageReportsPage.tsx)
  // — GET /messages/admin/message-reports, requireSuperAdmin-gated (no
  // organization_id on message_reports/direct_messages).
  messageReports: {
    // Bare prefix so the resolve/dismiss mutation can invalidate every
    // status-filter variant in one call, mirroring moderationReports.all above.
    all: () => ['message-reports'] as const,
    list: (status: string) => ['message-reports', 'list', status] as const,
  },
  // Spec 294 category 2. Multi-partner relationship list, org settings
  // (RelationshipsSection.tsx) — GET /organizations/:orgId/relationships.
  relationships: {
    list: (orgId: string) => ['relationships', orgId] as const,
  },
  // Spec 294 category 4 (pagination -> useInfiniteQuery). SharedWithMePage.tsx
  // -- orgId + the type-filter tab both fold into the key (mirrors
  // discovery.feed's "every request-shaping input in the key" reasoning) so
  // switching tabs or orgs is structurally a fresh cache entry/page-1, not a
  // stale accumulated list.
  sharedWithMe: {
    list: (orgId: string | undefined, typeFilter: string) => ['shared-with-me', orgId ?? null, typeFilter] as const,
  },
  // Spec 294 category 4. PraiseInbox.tsx's two tabs (received/sent) are
  // distinct endpoints with independent pagination -- separate keys, not one
  // key with a view param, so switching tabs doesn't invalidate the other
  // tab's already-fetched pages. `stats()` is its own query (pre-migration
  // loadData() fetched it alongside whichever list was active, refetching it
  // on every page turn even though it doesn't depend on `page` -- splitting
  // it out is a deliberate, noted improvement, see PraiseInbox.tsx).
  praise: {
    stats: () => ['praise', 'stats'] as const,
    received: () => ['praise', 'received'] as const,
    sent: () => ['praise', 'sent'] as const,
  },
  // Spec 297 Phase B batch B12 (praise/QuickPraiseModal.tsx). Bundles GET
  // /praise/templates + GET /organizations/:orgId/members into ONE query --
  // mirrors collaring.data's/sceneLog.dashboard's precedent of "one query
  // when pre-migration treats multiple endpoint calls as one fetch-and-render
  // unit" (pre-migration loadData() already fetches both via Promise.all
  // under one shared try/catch/loading flag).
  //
  // Deliberately its OWN top-level prefix ('quick-praise-context'), NOT
  // nested under `praise` above: QuickPraiseModal.tsx's own handleSend
  // already fires a pre-existing `queryClient.invalidateQueries({queryKey:
  // ['praise']})` on every successful send (to refresh PraiseInbox's
  // received/sent lists elsewhere) -- if this bundle shared that prefix, every
  // send would ALSO invalidate the templates+members it just read, and since
  // the modal is still mounted with `enabled: isOpen` true for the ~1.5s
  // success-screen window before auto-close, TanStack would fire an
  // unrequested background refetch right as the modal is closing. Pre-
  // migration never re-fetched templates/members after a send. A fully
  // distinct top-level key avoids that unintended coupling entirely (same
  // "avoid unintended coupling, use a fully distinct top-level key" call as
  // `identity.registrationResume`'s doc comment above).
  //
  // Not reusing `organizations.members(orgId)` for the members half either:
  // that key's existing consumer (useOrgMembersQuery.ts) sets
  // `staleTime: 300_000` with default retry (2), while this modal's
  // pre-migration loadData() had no retry wrapper at all (plain
  // Promise.all/try-catch, matching this batch's retry:false convention for
  // un-retried pre-migration fetches) and needs `enabled` gated on `isOpen`,
  // not just on orgId -- same per-query-options-collision reasoning as
  // `organizations.membersForSelect`'s doc comment (TanStack keeps exactly
  // one queryFn/options registration per key).
  //
  // NOT userId-scoped: confirmed via backend (praiseController.ts's
  // getTemplates -> praiseService.getPraiseTemplates(organizationId): `WHERE
  // organization_id IS NULL OR organization_id = $1`, no per-caller filter;
  // organizationController.ts's member list is likewise org-shared, not
  // per-viewer) -- both halves are genuinely org-shared data, not "my own"
  // data. `orgId` folded into the key (the modal's own `organizationId` prop)
  // even though GET /praise/templates itself takes no org param and instead
  // resolves the caller's org server-side -- same "fold in the context param
  // even though the endpoint doesn't take it explicitly" reasoning as
  // `negotiationTemplates.list`'s doc comment above.
  quickPraise: {
    context: (orgId: string) => ['quick-praise-context', orgId] as const,
  },
  // Spec 294 category 4. AdminAuditLogsPage.tsx -- no active filter/sort UI
  // today (the backend supports adminId/targetUserId/action params, but the
  // page never sends them), so the key is bare. Add the relevant param here
  // if filter UI is ever added.
  adminAuditLogs: {
    list: () => ['admin-audit-logs'] as const,
  },
  // Spec 309 (AdminAuthAuditLogPage.tsx) — distinct from adminAuditLogs above
  // (different table: auth_audit_log, not admin_audit_logs). Unlike that
  // page, this one has live filter UI from day one, so filters fold into the
  // key so each filter/offset combination caches independently.
  authAuditLog: {
    list: (filters: { userId?: number; eventType?: string; startDate?: string; endDate?: string; offset: number }) =>
      ['auth-audit-log', filters] as const,
  },
  // Spec 294 category 4. PopHistoryList.tsx is a per-task, per-limit widget
  // (props, not global filter state) -- both fold into the key so two
  // mounts for different tasks (or different `limit` props) never collide.
  popHistory: {
    forTask: (taskId: string, limit: number) => ['pop-history', taskId, limit] as const,
  },
  // Spec 297 Phase A batch 1 (CultivateBlockedUsers.tsx). GET
  // /discovery/blocked -- single caller-scoped list.
  //
  // SAFETY FIX (2026-08-04, Spec 297 Phase A post-merge fanout review,
  // MEDIUM -- design debt, not currently live-exploitable, see
  // `negotiations.active`'s SAFETY FIX comment above for the shared root
  // cause). Originally bare -- now scoped by userId.
  discoveryBlockedUsers: {
    list: (userId: string) => ['discovery-blocked-users', userId] as const,
  },
  // Spec 297 Phase A batch 1 (CultivateHierarchies.tsx). Public
  // organization browse/join -- distinct from organizations.publicList
  // above (that one's onboarding/organizations pages, different sort/filter
  // param shape: activity/members/newest/name sort here vs that key's
  // search/category/locationType/sort-string shape) and from
  // organizations.categories (that's the caller's OWN org's task
  // categories, not the public hierarchy-category taxonomy this page
  // browses). `list()` folds every request-shaping input (debounced search,
  // category, locationType, sort) into the key -- mirrors discovery.feed's
  // "every request-shaping input in the key" precedent, so TanStack's own
  // cancellation (see useQuery's injected `signal`, consumed then aborted
  // via Query.removeObserver when the key changes) replaces the
  // pre-migration hand-rolled AbortController ref.
  discoveryHierarchies: {
    categories: () => ['discovery-hierarchies', 'categories'] as const,
    list: (params: { search: string; category: string; locationType: string; sort: string }) =>
      ['discovery-hierarchies', 'list', params] as const,
  },
  // Spec 297 Phase A batch 1 (CultivateMatches.tsx). GET /discovery/matches
  // -- distinct from discoveryMessages.conversations() above (a different
  // endpoint: matched profiles, not conversation threads). CultivateMatches'
  // Messages tab reuses discoveryMessages.conversations() directly (see that
  // namespace's updated comment) rather than a third key for the same data.
  // SAFETY FIX (2026-08-04, Spec 297 Phase A post-merge fanout review,
  // MEDIUM -- design debt, not currently live-exploitable, see
  // `negotiations.active`'s SAFETY FIX comment above for the shared root
  // cause). Originally bare -- now scoped by userId.
  discoveryMatches: {
    list: (userId: string) => ['discovery-matches', userId] as const,
  },
  // Spec 297 Phase A batch 1 (CultivateProfile.tsx + CultivateSettings.tsx).
  // Both hit the identical GET /discovery/profile endpoint (getMyProfile) --
  // deliberately SHARED, not split like organizations.members/
  // membersForSelect above, because both consumers use the same retry:false,
  // no-staleTime-override options (no per-query-options conflict to isolate)
  // and sharing means navigating between the two pages doesn't double-fetch.
  // Each page hydrates its OWN local editable-form state from this cache via
  // a hydrate-ONCE effect (a ref guard, not a `[data]`-keyed effect) --
  // needed because both pages' mutations (photo upload/delete + profile
  // save in CultivateProfile) write back into this SAME cache entry via
  // setQueryData, which would otherwise re-run a naive `[data]`-dependent
  // hydration effect and silently stomp whatever the user was mid-typing.
  // activeState() is a separate endpoint (GET /discovery/active) used only
  // by CultivateSettings.
  //
  // SAFETY FIX (2026-08-04, Spec 297 Phase A post-merge fanout review,
  // MEDIUM x2 -- design debt, not currently live-exploitable, see
  // `negotiations.active`'s SAFETY FIX comment above for the shared root
  // cause). Both keys originally bare -- now scoped by userId. Both are read
  // via the shared hooks in useDiscoveryProfile.ts, which read the current
  // userId from useAuth() internally, so every consumer got the fix for
  // free with no call-site signature change beyond the mutation call sites
  // that write directly via setQueryData.
  discoveryProfile: {
    self: (userId: string) => ['discovery-profile', userId] as const,
    activeState: (userId: string) => ['discovery-profile', 'active-state', userId] as const,
  },
  // Spec 297 Phase A batch 7 (account-settings/ReferralSection.tsx). GET
  // /referrals/referral/status -- the caller's own referral-sharing status
  // (code, signup/reward counts). Same endpoint referralService.ts's
  // getReferralSharingStatus() wraps, but that function currently has zero
  // callers (see ReferralSection.tsx's own migration note) -- not reused
  // here to avoid coupling this migration to dead code's return shape.
  // SAFETY FIX (2026-08-04, Spec 297 Phase A post-merge fanout review,
  // MEDIUM -- design debt, not currently live-exploitable, see
  // `negotiations.active`'s SAFETY FIX comment above for the shared root
  // cause). Originally bare -- now scoped by userId.
  referral: {
    status: (userId: string) => ['referral-status', userId] as const,
  },
  // Spec 297 Phase A batch 1 (CultivateProfileDetail.tsx). GET
  // /discovery/profile/:userId (getPublicProfile) -- a VIEWED profile, keyed
  // by the VIEWED userId, distinct from discoveryProfile.self() above (the
  // caller's OWN profile, keyed by the CALLER's userId, different
  // endpoint/response shape entirely).
  discoveryProfileDetail: {
    public: (userId: string) => ['discovery-profile-detail', userId] as const,
  },
  // Spec 297 Phase A batch 1 (CultivateHierarchies.tsx's verification-level
  // gate). Not discovery-scoped -- GET /verification/status is an
  // account-level endpoint also read by VerificationSection.tsx (migrated
  // Spec 297 Phase A batch 7) and useRegister.ts (migrated Spec 297 Phase A
  // batch 8, but via a distinct `registrationAvailability` key -- see below).
  //
  // UPDATE (batch 7): evaluated sharing this key per this comment's own
  // stated intent -- CultivateHierarchies needs only the derived `.level`
  // string, VerificationSection needs the full `{user, services}` object,
  // so a widened shared queryFn was structurally possible (one fetch, two
  // projections). Split into a separate key instead (`fullStatus` below)
  // because the two consumers need incompatible per-query options, not just
  // different shapes: VerificationSection's `handleStartBiometric` redirects
  // to Didit and back to /settings within seconds, so it needs
  // `staleTime: 0` to guarantee the post-redirect remount shows the just-
  // completed verification rather than a warm ~30s-stale cache entry (a
  // real, user-visible correctness requirement); CultivateHierarchies is
  // fine with the global default. Per this file's own documented precedent
  // (organizations.members/membersForSelect above: "TanStack Query has
  // exactly one options set per cache entry -- whichever hook mounted first
  // silently won for both"), sharing this key would risk VerificationSection
  // silently losing its staleTime:0 guarantee if CultivateHierarchies'
  // observer happened to win. Splitting removes that risk entirely, same
  // reasoning as that precedent.
  // SAFETY FIX (2026-08-04, Spec 297 Phase A post-merge fanout review,
  // MEDIUM x2 -- design debt, not currently live-exploitable, see
  // `negotiations.active`'s SAFETY FIX comment above for the shared root
  // cause). Both `status` and `fullStatus` below were originally bare -- now
  // scoped by userId. `registrationAvailability` below is UNCHANGED
  // (already its own distinct key, see its own comment; also not
  // genuinely per-user data in the same sense -- provider-availability
  // booleans, not the caller's own verification state).
  verification: {
    status: (userId: string) => ['verification-status', userId] as const,
    // Spec 297 Phase A batch 7 (VerificationSection.tsx). Full status object
    // (`{user: {level, emailVerified, phoneVerified, biometricVerified},
    // services: {didit, twilio}}`) from the SAME GET /verification/status
    // endpoint as `status` above -- see this namespace's comment for why
    // it's a separate key rather than a shared one.
    //
    // Historical note: before the userId SAFETY FIX below, this key was a
    // prefix-descendant of `status()` above (['verification-status'] vs
    // ['verification-status', 'full']) -- a future
    // `invalidateQueries({queryKey: queryKeys.verification.status(userId)})`
    // would, under default non-exact matching, have cascade-invalidated this
    // key too. Inserting userId at different positions (`status` ->
    // ['verification-status', userId]; `fullStatus` ->
    // ['verification-status', 'full', userId]) breaks that prefix
    // relationship -- harmless either way (no call site relied on the
    // cascade, and both keys represent the same underlying endpoint), noted
    // so it isn't a surprise later.
    //
    // SAFETY FIX (2026-08-04, Spec 297 Phase A post-merge fanout review):
    // scoped by userId, see this namespace's top comment.
    fullStatus: (userId: string) => ['verification-status', 'full', userId] as const,
    // Spec 297 Phase A batch 8 (useRegister.ts's checkVerificationProviders
    // effect). Same GET /verification/status endpoint as `status` above,
    // but evaluated for reuse and rejected: this queryFn is a THIRD
    // incompatible shape -- on failure it falls back to an entirely
    // different endpoint (ageVerificationAPI.checkDiditStatus(), GET
    // /age-verification/didit/status) rather than surfacing an error, and
    // projects the result to a `{diditAvailable, twilioAvailable}` boolean
    // pair, not `status`'s derived `.level` string. A different queryFn
    // body can't safely share a cache key with `status` (same reasoning
    // this file uses throughout -- see organizations.membersForSelect's
    // doc comment for the canonical statement of why), so this is a fully
    // distinct top-level key, not a prefix-descendant of `status` above.
    // Not a top-level namespace on its own because it's the same
    // /verification/status endpoint family, just a third incompatible shape.
    // retry:false -- pre-migration was a single try/catch (with its own
    // nested try/catch fallback) and no retry loop.
    registrationAvailability: () => ['verification-registration-availability'] as const,
  },
  // Spec 297 Phase A batch 6 (useBillingSettings.ts). GET /storage/warnings
  // (storageManagementAPI.getStorageWarnings, no orgId param). Skeptical-
  // review correction (2026-08-04): the backend does NOT resolve "the
  // currently-viewed org" here -- getStorageWarnings resolves the org via
  // getUserOrganization(userId) (authorizationService.ts), which
  // deterministically returns the user's lowest-organization_id membership
  // ("primary org"), ignoring whichever org's settings page is actually
  // open. This is a separate, pre-existing product GAP in bug-registry.md
  // (org-blindness for multi-org admins), not fixed here.
  // SAFETY FIX (Spec 297 Phase B batches B10+B11's own skeptical review):
  // this key was bare (no userId) despite genuinely varying by CALLER
  // identity (getUserOrganization(userId) resolves a different org per
  // user, not just per-org) -- same cross-user cache-leak class as every
  // other userId-scoped key in this file (AuthContext's login()/register()
  // don't queryClient.clear()). Now userId-scoped; both live consumers
  // (useBillingSettings.ts, components/StorageWarningBanner.tsx) updated
  // together.
  storage: {
    // Reused as-is by components/StorageWarningBanner.tsx (Spec 297 Phase B
    // batches B10+B11) -- confirmed identical queryFn
    // (storageManagementAPI.getStorageWarnings() -> unwrapResponse) and
    // `retry: false` at both this and useBillingSettings.ts's call sites;
    // the two hooks' generic types differ (StorageWarning is a superset of
    // StorageInfo's fields) but both are views onto the same unwrapped
    // response object, not different queryFn bodies.
    warnings: (userId: string) => ['storage-warnings', userId] as const,
  },
  // Structural-debt backlog #8 (pages/storage-management/useStorageManagement.ts
  // -- the org-admin storage management page, distinct from
  // userStorageManagement above which is the per-user page). All of these
  // storageManagementAPI endpoints take no orgId param and resolve the
  // caller's org server-side the same way getStorageWarnings does (see
  // `storage.warnings`'s own doc comment on that pre-existing org-blindness
  // product gap) -- userId-scoped here for the same cross-account-switch
  // cache-leak reasoning as every other "my own data" key in this file.
  storageManagementAdmin: {
    retentionInfo: (userId: string) => ['storage-mgmt-admin', 'retention-info', userId] as const,
    breakdown: (userId: string) => ['storage-mgmt-admin', 'breakdown', userId] as const,
    recommendations: (userId: string) => ['storage-mgmt-admin', 'recommendations', userId] as const,
    // A fourth caller of organizationAPI.getDetails(orgId) -- own dedicated
    // key, same precedent as hierarchySectionOrgDetails/settingsCoreOrgDetails
    // above (this one reads only .userRole).
    orgRole: (orgId: string) => ['storage-mgmt-admin', 'org-role', orgId] as const,
    files: (userId: string, params: { page: number; search: string; sortBy: string; sortOrder: string }) =>
      ['storage-mgmt-admin', 'files', userId, params] as const,
    recycleBin: (userId: string) => ['storage-mgmt-admin', 'recycle-bin', userId] as const,
    analytics: (userId: string, period: string) => ['storage-mgmt-admin', 'analytics', userId, period] as const,
    deletionPreview: (userId: string) => ['storage-mgmt-admin', 'deletion-preview', userId] as const,
  },
  // Spec 297 Phase B batch B2 (StreaksPage.tsx). Prefix ('streaks') matches
  // the page's own PRE-EXISTING `queryClient.invalidateQueries({ queryKey:
  // ['streaks'] })` call in handleUseProtection (previously inert -- nothing
  // was cached under this prefix before this migration; same pattern as
  // points/protocols/chastity/orgasmBank above).
  //
  // helm-skeptical-evaluator HIGH finding (2026-08-05): this key was
  // originally org-scoped only, citing chastity.dashboard(orgId)/
  // points.dashboard(orgId) as precedent -- but that "precedent" turned out
  // to be an un-audited instance of the SAME open bug (verified directly:
  // points.dashboard bundles the user's own balance/transactions/purchases
  // under an org-only key too), and this data genuinely includes the
  // caller's OWN streak stats (userStreak, achieved milestones, totalPoints,
  // daysToNextMilestone) -- not just the org-shared leaderboard. Per the
  // still-open bug-registry.md GAP (~line 5167): AuthContext.tsx's login()/
  // register() do not call queryClient.clear() (only logout() does), so a
  // same-tab account switch without an intervening logout can otherwise
  // serve a prior user's cached streak data to whoever logs in next. Now
  // userId-scoped, matching this same diff's already-correct
  // rewards.mine(userId)/mediaBilling.status(userId) pattern.
  streaks: {
    dashboard: (orgId: string, userId: string) => ['streaks', orgId, 'dashboard', userId] as const,
  },
  // Spec 297 Phase B batch B2 (RewardsPage.tsx). GET /rewards/my + GET
  // /rewards/stats, combined via Promise.all (mirrors sceneLog.dashboard/
  // collaring.data's precedent of bundling multiple endpoint calls the
  // pre-migration loadData() already treated as one fetch-and-render unit).
  // Genuinely "my rewards" (recipient-scoped) -- scoped by userId per the
  // Phase A post-merge SAFETY FIX precedent (see `negotiations.active`'s
  // comment for the shared root cause: AuthContext's login() doesn't clear
  // the QueryClient, so a same-tab account switch could otherwise read a
  // prior user's cached reward list).
  rewards: {
    mine: (userId: string) => ['rewards', 'mine', userId] as const,
    // Spec 297 Phase B batch B7 (RewardAuditModal.tsx). GET
    // /rewards/:rewardId/views -- deliberately NOT userId-scoped, unlike
    // `mine` above. rewardController.ts's getRewardViews enforces `WHERE id
    // = $1 AND sender_id = $2` (sender-only; confirmed by reading the
    // controller directly) and RewardCard.tsx only ever mounts this modal
    // behind `isSender = reward.senderId === user.id` -- a reward's sender
    // never changes and rewardId is a globally-unique internal id, so there
    // is no scenario where two different users' sessions could legitimately
    // resolve the SAME cache entry to two different expected payloads (the
    // cross-user-leak precondition every userId-scoped key above this one
    // is guarding against). Keying by rewardId alone is the entity-owned
    // pattern (like a task-detail fetch), not the "my own account feed"
    // pattern `mine` is.
    auditViews: (rewardId: number) => ['rewards', 'audit-views', rewardId] as const,
  },
  // Spec 297 Phase B batch B2 (MediaBillingPage.tsx). GET
  // /media/billing/status + GET /media/billing/plans + GET
  // /media/billing/trial/check, combined via Promise.all (same bundling
  // precedent as `rewards.mine`/`streaks.dashboard` above). This is the
  // PERSONAL Media Library subscription (`mediaBillingController.ts`,
  // resolved via the caller's own userId server-side) -- NOT the same
  // surface as the org billing controller (`billingController.ts`,
  // CheckoutPage.tsx's `billingAPI.createCheckout`/`createPortal`) that has
  // the documented org-resolution bug noted in this batch's sprint contract
  // (bug-registry.md's 2026-08-04/05 billing entries); that bug class does
  // not apply here since this endpoint was never org-scoped to begin with.
  // Two of the three bundled fetches (status, trial availability) are
  // genuinely per-user; `plans` is a static catalog -- bundled under one
  // userId-scoped key anyway rather than split, mirroring
  // discoveryProfile.self's precedent of bundling a per-user fetch with
  // catalog-shaped data under one key when the pre-migration code always
  // fetched them together as a unit.
  mediaBilling: {
    status: (userId: string) => ['media-billing', 'status', userId] as const,
  },
  // Spec 297 Phase B batch B1 (AcceptInvitationPage.tsx). GET
  // /auth/check-invitation?token=... -- public, pre-login page. Keyed by the
  // invitation token itself (not userId): the token IS the scoping secret
  // here (two different invitations always have different tokens; the same
  // token always resolves to the same invitation for whoever holds it), so
  // there is no cross-user cache-bleed vector of the kind the `userId`
  // SAFETY FIX comments elsewhere in this file describe -- unlike those
  // cases, this data was never "the caller's own" ambient state to begin
  // with.
  invitations: {
    check: (token: string) => ['invitations', 'check', token] as const,
  },
  // Spec 297 Phase B batch B1 (AgeVerificationPage.tsx). GET
  // /age-verification/status -- "am I (the caller) age-verified", the same
  // "own account state" shape as verification.status/fullStatus above.
  // Proactively scoped by userId from the start (not added after review) --
  // this hook is called from the account's OWN verification-status check;
  // AuthContext's login() doesn't clear the QueryClient (see
  // identity.registrationResume's SAFETY FIX comment for the full root
  // cause), so a bare key here would be the exact same cross-user same-tab
  // leak class already fixed across every other genuinely-per-user key in
  // this migration.
  ageVerification: {
    status: (userId: string) => ['age-verification-status', userId] as const,
  },
  // Spec 297 Phase B batch B1 (VerifyEmailPage.tsx). GET
  // /auth/verify-email/:token -- public route, no PrivateRoute wrapper (the
  // token itself, not an authenticated session, is what authorizes this
  // action). Keyed by the token, same reasoning as `invitations.check`
  // above -- not ambient per-user state, the token is the whole scope.
  emailVerification: {
    verify: (token: string) => ['email-verification', token] as const,
  },
  // Spec 297 Phase B batch B1 (AppLockRecovery/index.tsx). POST
  // /users/app-lock-recovery/verify -- fully public/unauthenticated page (no
  // PrivateRoute, no useAuth() call in this file at all). Keyed by the
  // one-time recovery code from the URL, same reasoning as
  // `invitations.check`/`emailVerification.verify` above.
  appLockRecovery: {
    verify: (code: string) => ['app-lock-recovery-verify', code] as const,
  },
  // Spec 297 Phase B batch B1 (safeword/useSafeword.ts). Bundles
  // getMySafewords + getLogs/getMyLogs + getStats + getUnacknowledgedCount
  // (Promise.all) into ONE query, mirroring sceneLog.dashboard/
  // collaring.data's precedent of bundling a page's whole load into one
  // cache entry. `isAdmin` folds into the key because it changes BOTH which
  // endpoint fires (getLogs vs getMyLogs) AND the response shape/scope
  // (admin sees every subordinate's logs; a non-admin sees only their own) --
  // same "different shape = different key" reasoning as
  // organizations.membersForSelect above, just folded into one key via a
  // parameter instead of a second key. `orgId` because this is safety-
  // critical data server-scoped to the active Hierarchy (see this file's own
  // pre-migration comment on `latestOrgIdRef` in useSafeword.ts -- switching
  // Hierarchy via the header chip must not go on showing the previous
  // Hierarchy's safeword state; a bare cache key already gets this for free
  // via TanStack's own per-key caching, no ref-guard needed post-migration).
  // `userId` scoped PROACTIVELY (not as a post-review SAFETY FIX like most
  // of this file's other userId-scoped entries) -- this is explicitly
  // called out as a consent/safety-adjacent surface for this batch, and
  // mySafewords/logs/unacknowledgedCount are genuinely "my own" data by the
  // same root-cause reasoning as every SAFETY FIX comment in this file
  // (AuthContext's login() doesn't clear the QueryClient; a same-tab account
  // switch must never surface a prior user's cached safeword state).
  //
  // Deliberately kept prefixed with 'safeword' (not a new unrelated root) --
  // useSafeword.ts's own handleTrigger/handleAcknowledge/handleResumeLog/
  // handleSaveSafewords already call
  // `queryClient.invalidateQueries({ queryKey: ['safeword'] })` today, pre-
  // dating this migration and previously inert (nothing was ever cached
  // under that key). Same "previously-inert prefix becomes real" pattern as
  // chastity.all/protocols.all/points.all/orgasmBank.all/features.all above.
  safeword: {
    dashboard: (orgId: string, userId: string, isAdmin: boolean) =>
      ['safeword', 'dashboard', orgId, userId, isAdmin] as const,
    // bug-registry 0zzz-au: GET /safeword/aftercare/:logId, used by both the submissive's
    // check-in screen (AftercareCheckinPage) and the linked-Dominant's read-only Sheet
    // (SafewordHistory's aftercare status row). Entity-by-id, mirrors messages.conversation's
    // "single named entity" precedent above, not the userId-scoped dashboard shape -- logId
    // itself is already the access-scoping unit (backend authorizes isSelfOrLinkedDominant per
    // logId, not per caller identity), so a bare logId key is correct here.
    aftercare: (logId: string) => ['safeword', 'aftercare', logId] as const,
    // GET /safeword/config (SoloSelfConfirmView.tsx). "My own" safeword
    // config (soft/hard limit words) -- userId-scoped, same discipline as
    // dashboard above.
    config: (userId: string) => ['safeword', 'config', userId] as const,
  },
  // GET /scene-plans/:id/limits-check (SoloSelfConfirmView.tsx). Entity-by-id
  // -- access is authorized per plan ownership server-side, mirrors
  // safeword.aftercare(logId)'s "single named entity" precedent.
  scenePlanLimitsCheck: {
    forPlan: (planId: string) => ['scene-plan-limits-check', planId] as const,
  },
  // Structural-debt backlog #8, DiditTestPage.tsx (internal debug/test page,
  // not user-facing). `status` hits GET /age-verification/didit/status
  // directly and returns the full raw config-status object -- the SAME
  // endpoint verification.registrationAvailability()'s fallback path calls
  // via ageVerificationAPI.checkDiditStatus(), but that key's queryFn
  // projects the response down to a `{diditAvailable, twilioAvailable}`
  // boolean pair. Different queryFn shape can't share a cache key (see
  // organizations.membersForSelect's doc comment) -- distinct key here.
  // `result` is a separate per-session endpoint (GET
  // /age-verification/didit/result/:sessionId).
  diditTest: {
    status: () => ['didit-test-status'] as const,
    result: (sessionId: string) => ['didit-test-result', sessionId] as const,
  },
  // Spec 297 Phase B batch B1 (MessagesPage.tsx's Spec 260 deep-link fetch).
  // GET /messages/conversations/:conversationId -- a single named entity
  // fetched by its own id (mirrors journal.entry/chastity.healthChecks'
  // "entity-by-id" precedent, not the "my own list" shape that gets
  // userId-scoped elsewhere in this file). conversationId itself is the
  // scoping key; two different users landing on the same conversationId URL
  // is the SAME conversation for both of them (or a 403/404 on the fresh
  // network call for whichever of them isn't a participant), not a
  // cross-user leak of one user's private data into another's cache.
  // Distinct from messages.thread(conversationId) above (that one is the
  // message list itself, owned by MessageThread.tsx, unmigrated/out of
  // scope for this batch).
  messagesDeepLink: {
    conversation: (conversationId: string) => ['messages', 'conversation-detail', conversationId] as const,
  },
  // Spec 297 Phase B batch B4 (TaskAuditLogPage.tsx). GET /tasks/:id/audit-logs
  // -- task-scoped history, visible to any hierarchy member with view access
  // to the task itself (backend-authorized per task, not per-user). Kept as a
  // descendant of the 'tasks' prefix (not a new top-level namespace) so
  // useTaskDetail.ts's existing bare `invalidateQueries({queryKey:['tasks']})`
  // call, already fired on task mutations, also refreshes a task's audit
  // trail -- correct, since an audit entry is created by the very mutations
  // that prefix already targets.
  //
  // No userId scoping: this is not "my own" ambient account state (the
  // SAFETY FIX pattern used throughout this file for that class of data) --
  // it's a shared, task-scoped historical record every authorized viewer of
  // THIS task sees identically.
  tasksAuditLog: {
    forTask: (taskId: string) => ['tasks', 'audit-log', taskId] as const,
  },
  // Spec 297 Phase B batch B4 (connect/SharedTemplatesPage.tsx). GET
  // /protocol-templates/shared?search=&limit=50 (app.ts's direct
  // listGlobalSharedTemplates route, NOT protocolRoutes.ts's org-scoped
  // /protocols/templates). Confirmed via
  // controllers/community/protocolSharingController.ts: getGloballySharedTemplates
  // takes only {limit, search} -- no userId/orgId param, genuinely global and
  // identical for every caller, so no userId scoping needed (org-shared/
  // browse-type data, not "my own" state). `search` folds into the key
  // (debounced copy, mirrors discoveryHierarchies.list/discovery.feed's
  // "every request-shaping input in the key" precedent) so each distinct
  // search term is its own cache entry.
  sharedTemplates: {
    list: (search: string, page: number = 1) => ['shared-templates', 'list', search, page] as const,
  },
  // Spec 297 Phase B batch B4 (relationship-reviews/useRelationshipReviews.ts).
  // Combined reviews + open/completed instances + org members + org settings
  // (Promise.all), mirroring sceneLog.dashboard/collaring.data/rewards.mine's
  // precedent of bundling a page's whole load into one cache entry since the
  // pre-migration loadData() already treated it as one fetch-and-render unit.
  // SAFETY FIX (found in this batch's own skeptical review): the original
  // comment here claimed reviews/instances were shared identically between
  // both hierarchy partners, so the key was org-scoped only. False for orgs
  // with more than 2 members -- backend's getUserReviews/getOpenInstancesForUser
  // /getCompletedInstancesForUser (relationshipReviewService.ts) all filter
  // WHERE organization_id = $1 AND (dominant_id = $2 OR submissive_id = $2)
  // with $2 = the CALLING user's id, so two non-dyad-partner members of the
  // same org get genuinely different result sets -- same bug class as the
  // Spec 279 S2 incident (contractService.ts). Combined with AuthContext's
  // login()/register() not calling queryClient.clear(), a same-tab account
  // switch within staleTime could render a prior user's private review
  // titles/discussion threads. Now userId-scoped like the rest of this
  // file's "my own account state" keys.
  relationshipReviews: {
    dashboard: (orgId: string, userId: string) =>
      ['relationship-reviews', 'dashboard', orgId, userId] as const,
  },
  // Spec 297 Phase B batch B4 (user-storage-management/useUserStorageManagement.ts).
  // GET /media/storage-management/* -- confirmed via
  // userStorageManagementController.ts: every handler reads `req.user.id`
  // directly, no organization scoping at all. Genuinely personal storage
  // (files, recycle bin, bulk-delete-by-age) -- distinct from BOTH
  // `mediaLibrary` below (a different page/endpoint prefix, `/media/*`, see
  // that namespace's comment) and `storage.warnings` above (a THIRD distinct
  // endpoint, `/storage/warnings`, org-blind-by-primary-org). Proactively
  // scoped by userId (not a post-review SAFETY FIX) -- this is exactly the
  // "my own account data" shape the SAFETY FIX precedent throughout this file
  // exists to protect.
  userStorageManagement: {
    overview: (userId: string) => ['user-storage-management', 'overview', userId] as const,
    files: (userId: string, params: { page: number; search: string; sortBy: string; sortOrder: string; type: string | undefined }) =>
      ['user-storage-management', 'files', userId, params] as const,
    recycleBin: (userId: string) => ['user-storage-management', 'recycle-bin', userId] as const,
  },
  // Spec 297 Phase B batch B4 (platform-config/usePlatformConfig.ts). Platform
  // super_admin config surface (route-gated on `currentUser.role ===
  // 'super_admin'`, the platform-level role, not an org role) -- flags,
  // limits, the admin site-banner manager, and admin/org select-list
  // dropdowns are all genuinely platform-GLOBAL, identical for whichever
  // super_admin is viewing. Not the "my own account" class of data the
  // userId SAFETY FIX pattern elsewhere in this file protects against, so no
  // userId scoping. `banner` is deliberately NOT `siteBanner.active()` above
  // -- same GET-adjacent domain (site banners) but a different endpoint
  // (admin's `/admin/site-banner` returns `{active, history}`; the public
  // `siteBanner.active()` hits `/site-banner/active`, active-only), see that
  // key's own file (components/SiteBanner.tsx) for its distinct shape.
  platformConfig: {
    data: () => ['platform-config', 'data'] as const,
    selectLists: () => ['platform-config', 'select-lists'] as const,
    banner: () => ['platform-config', 'banner'] as const,
  },
  // Spec 297 Phase B batch B4 (media-library/useMediaLibrary.ts). GET
  // /media/{items,tags,albums,storage} + /media/billing/{status,trial/check}
  // -- confirmed via mediaLibraryController.ts: every handler reads
  // `req.user.id` directly (getAlbums even passes it twice, once as
  // requesterId for proxy URL emission), no org scoping. Kept as FOUR
  // independent queries (not one bundled dashboard query) rather than
  // consolidated, because the pre-migration hook already treats them as
  // independent: `loading` is derived ONLY from the items fetch (tags/
  // albums/storage/billing loading state was never surfaced to the caller
  // pre-migration either) -- bundling them would change what drives the
  // returned `loading` flag, a real behavior change this migration must not
  // introduce. `billing` bundles status+trial (Promise.all, matching
  // pre-migration's loadBillingStatus()) -- deliberately NOT a reuse of
  // `mediaBilling.status(userId)` (Spec 297 Phase B batch B2,
  // MediaBillingPage.tsx): that key's queryFn ALSO bundles `plans`, a third
  // endpoint this hook never calls -- different queryFn shape, so per this
  // file's own membersForSelect/verification.fullStatus precedent ("a
  // different queryFn body can't safely share a cache key"), this gets its
  // own key.
  //
  // First element deliberately 'media' (not 'media-library') so the
  // pre-existing `queryClient.invalidateQueries({queryKey:['media']})` calls
  // -- both this file's OWN post-mutation calls (upload/delete/create-album/
  // create-tag, unchanged by this migration) and storage-management/
  // useStorageManagement.ts's (a separate, still-unmigrated sibling hook,
  // out of this batch's scope) -- become real invalidation targets instead
  // of the previously-inert prefix they always were pre-migration. Same
  // "previously-inert prefix becomes real" pattern as chastity.all/
  // points.all/protocols.all/orgasmBank.all/features.all/safeword's
  // 'safeword' prefix elsewhere in this file.
  mediaLibrary: {
    items: (userId: string, filters: { filterType: string; selectedTags: number[]; searchQuery: string; page: number }) =>
      ['media', 'library-items', userId, filters] as const,
    tags: (userId: string) => ['media', 'library-tags', userId] as const,
    albums: (userId: string) => ['media', 'library-albums', userId] as const,
    storage: (userId: string) => ['media', 'library-storage', userId] as const,
    billing: (userId: string) => ['media', 'library-billing', userId] as const,
  },
  // Spec 297 Phase B batch B3 (SessionsPage.tsx). Bundles GET /auth/sessions
  // (sessionsAPI.getSessions()) + GET /auth/profile (authAPI.getProfile(),
  // read only for .loginNotificationsEnabled) into ONE composite query,
  // mirroring safeword.dashboard/collaring.data's precedent of bundling a
  // page's whole on-mount load into one cache entry. Deliberately NOT a
  // reuse of auth.profile() above even though it hits the identical
  // authAPI.getProfile() endpoint -- that key's queryFn is fetched via
  // imperative queryClient.fetchQuery({staleTime:0}) calls from
  // AuthContext.tsx with no live useQuery observer (see its own doc
  // comment); mounting a second, differently-optioned useQuery observer on
  // the same key would hit the exact "TanStack Query has exactly one
  // options set per cache entry" footgun this file's own
  // organizations.membersForSelect/membersOnly split documents. userId-
  // scoped: both halves of this bundle are genuinely "my own account" data
  // (my active device sessions, my notification preference) -- same SAFETY
  // FIX reasoning as every other userId-scoped key in this file
  // (AuthContext's login() doesn't clear the QueryClient).
  sessions: {
    list: (userId: string) => ['sessions', 'list', userId] as const,
  },
  // Spec 297 Phase B batch B3 (MentorshipPage.tsx). 3 independent
  // tab-gated queries, mirroring useProtocols.ts's precedent (categoriesQuery/
  // protocolsQuery/myProtocolsQuery) of splitting a tab-conditional
  // hand-rolled loadData() into separate queries with `enabled` gates rather
  // than one composite -- each tab populates different state under a
  // different cache key, and a composite key can't distinguish "not yet
  // fetched" per tab.
  mentorship: {
    // GET /mentorships/my -- the caller's own mentorships (both as mentor
    // and mentee). userId-scoped: genuinely "my own" list, same SAFETY FIX
    // reasoning as every other userId-scoped key in this file.
    mine: (userId: string) => ['mentorship', 'mine', userId] as const,
    // SAFETY FIX (found during the Spec 297 Phase B integration checkpoint's
    // qa-fanout sweep, a 9th live instance of this session's recurring
    // cross-user cache-leak class). The reasoning this key USED TO carry --
    // "discovery/browse surface, not ambient per-user data" -- undersold
    // what the trust/community filter actually does: mentorshipService.ts's
    // getVisibleMentors(userId, ...) doesn't just personalize ranking, it
    // bidirectionally EXCLUDES any mentor blocked-by-or-blocking the caller
    // (`NOT EXISTS ... user_blocks WHERE (blocker_id=$1 AND
    // blocked_id=mp.user_id) OR (blocker_id=mp.user_id AND blocked_id=$1)`)
    // -- a real safety mechanism, not mere personalization. Same shape as
    // messages.userSearch below (batch B10+B11, landed one batch later,
    // correctly userId-scoped for the identical "excludes blocked users"
    // reasoning) -- this key reasoned its way to the opposite conclusion
    // for the same underlying property. Combined with AuthContext's
    // login()/register() not calling queryClient.clear(), a same-tab
    // account switch could show a mentor the new user has blocked (or who
    // has blocked them), or a mentor visible only via the PRIOR user's
    // community membership. Now userId-scoped.
    available: (search: string, userId: string) => ['mentorship', 'available', search, userId] as const,
    // GET /mentors/profile -- "is the caller a mentor, and if so what's
    // their own profile/trust level". Genuinely "my own" account state --
    // userId-scoped, same SAFETY FIX reasoning as `mine` above.
    myProfile: (userId: string) => ['mentorship', 'my-profile', userId] as const,
    // GET /mentors/by-focus (ChannelMentorSuggestions.tsx). SAFETY: same
    // userId-scoping requirement as `available` above — mentorshipService.ts's
    // getMentorsByFocusArea(userId, ...) bidirectionally excludes blocked
    // users, so this is personalized filtering, not a channel-scoped public
    // list. userId-scoped from this key's introduction (not a later fix).
    byFocusArea: (focusArea: string, userId: string) => ['mentorship', 'by-focus', focusArea, userId] as const,
  },
  // Spec 297 Phase B batch B3 (contracts/ContractView.tsx). Entity-by-id
  // sub-resources of a single contract (mirrors chastity.healthChecks(lockId)/
  // journal.entry's "entity-by-id" precedent, not the userId-scoped shape --
  // two different users viewing the same contractId is the SAME contract's
  // milestones/drift for both of them, gated server-side by contract
  // membership, not a cross-user leak). Both descendants of the existing
  // bare `contracts.all` (`['contracts']`) prefix above, so useContracts.ts's
  // pre-existing invalidation of that prefix also reaches these (harmless
  // extra coverage -- these weren't coupled to the contract-list refresh
  // pre-migration, but there's no correctness reason to keep them
  // uninvalidated by it either).
  contractDetail: {
    milestones: (contractId: number) => ['contracts', 'milestones', contractId] as const,
    drift: (contractId: number) => ['contracts', 'drift', contractId] as const,
  },
  // Spec 297 Phase B batch B3 (rituals/useRituals.ts). Bundles getTemplates()
  // + getMyStats() + getLeaderboard(10) (Promise.all) into ONE query,
  // mirroring safeword.dashboard/sceneLog.dashboard's precedent of bundling
  // a page's whole on-mount load into one cache entry. Prefix ('rituals')
  // matches this hook's own pre-existing `queryClient.invalidateQueries({
  // queryKey: ['rituals']})` calls on every mutation (previously inert -- no
  // cache existed under that key -- now a real invalidation target, same
  // "previously-inert prefix becomes real" pattern as chastity.all/
  // protocols.all/points.all/orgasmBank.all/features.all/safeword.dashboard
  // above). userId-scoped alongside orgId: getMyStats() is genuinely "my
  // own" data bundled together with org-shared templates/leaderboard in one
  // cache entry -- same mixed-scope reasoning as safeword.dashboard's
  // isAdmin-in-key precedent (there the mixed factor is admin-vs-own scope;
  // here it's userId folded in because one piece of the bundle is
  // unambiguously "my own" data), same SAFETY FIX root cause as every other
  // userId-scoped key in this file.
  rituals: {
    dashboard: (orgId: string, userId: string) => ['rituals', orgId, userId] as const,
  },
  // Spec 297 Phase B batch B3 (checklist/NegotiationsTab.tsx). Consent/
  // limits-adjacent surface -- extra scrutiny applied, same as B1's
  // safeword/useSafeword.ts.
  negotiationsTab: {
    // GET /limits/negotiations/active -- the SAME endpoint as
    // `negotiations.active(userId)` above (NegotiationStatusCard.tsx,
    // Phase A batch 5), but deliberately a SEPARATE key, not a reuse.
    // NegotiationsTab.tsx's own pre-existing comment (preserved below on
    // the query itself) documents a real, previously-fixed bug (Spec 287
    // QA pass, helm-safety-reviewer): this page stays mounted across a
    // Hierarchy switch via the always-visible header chip, and the active
    // negotiation is server-scoped to whichever org is active even though
    // getActiveNegotiation() takes no explicit orgId param. Pre-migration,
    // that was patched with a `latestOrgIdRef` guard that discards a
    // response resolving after a further org switch, plus `organization?.id`
    // in loadNegotiations' deps to force a refetch on switch. Folding orgId
    // into THIS key removes the need for that guard entirely: TanStack's
    // own per-key caching means an org switch is a different cache entry
    // outright, and a stale in-flight response for the OLD orgId's key
    // resolves into the OLD key's cache slot, never bleeding into the new
    // org's -- the exact "gets this for free via TanStack's own per-key
    // caching, no ref-guard needed post-migration" precedent
    // safeword.dashboard's doc comment above already established for the
    // identical race class. NegotiationStatusCard.tsx's own usage doesn't
    // need this (it's scoped to one training-program enrollment, not a
    // persistent org-switchable tab), which is why THAT key stays
    // userId-only -- splitting here, rather than adding orgId to the shared
    // key, avoids changing NegotiationStatusCard's already-shipped cache
    // shape for a race condition specific to this page.
    active: (orgId: string, userId: string) => ['negotiations-tab', 'active', orgId, userId] as const,
    // GET /limits/negotiations/history -- on-demand (History button),
    // org-scoped (same active-org server-scoping as `active` above).
    history: (orgId: string) => ['negotiations-tab', 'history', orgId] as const,
    // GET /limits/discussions/:activityId -- discussion thread for one
    // overlap activity row within a revealed negotiation's results.
    //
    // Keyed by (negotiationId, activityId) as of bug-registry 0zzz-af — the
    // prior activityId-only key (see this comment's own history: "activityId
    // uniquely identifies the discussion thread regardless of which
    // negotiation... it surfaced under") was the exact assumption that
    // caused the bug: a Dominant linked to multiple submissives saw every
    // linked sub's notes on the same activity pooled into ONE cache entry
    // (and, server-side, one query result) instead of a separate thread per
    // pair. selectedNeg.id (the negotiation currently being viewed) is
    // always in scope wherever this key is used — the discussion panel only
    // ever renders inside NegotiationsTab.tsx's `if (selectedNeg)` branch.
    discussions: (negotiationId: number, activityId: number) => ['negotiations-tab', 'discussions', negotiationId, activityId] as const,
    // GET /limits/negotiations/:id/copilot (Spec 312 — Negotiation Copilot,
    // feature-flag gated). Literal ['negotiation-copilot', negotiationId] on
    // purpose -- the spec's own Mandatory Patterns Checklist hardcodes this
    // exact invalidation key (`queryClient.invalidateQueries({ queryKey:
    // ['negotiation-copilot', negotiationId] })` after any discussion-note
    // add), so this factory's return value must match it verbatim rather
    // than the 'negotiations-tab'-prefixed convention the rest of this block
    // uses. No orgId/userId scoping needed -- the endpoint is authorized
    // per-negotiation server-side (getNegotiationResult()'s participant
    // check), and a revealed negotiation's copilot output is immutable
    // except for discussion-note adds, which this exact key already covers.
    negotiationCopilot: (negotiationId: number) => ['negotiation-copilot', negotiationId] as const,
  },
  // Spec 297 Phase B batches B10+B11 (journal/PromptBanner.tsx,
  // journal/PromptAssignModal.tsx). Distinct top-level namespace from
  // `journal` above (journalAPI -- entries/moods/scenes) -- journalPromptAPI
  // is a different endpoint family entirely (Dom-assigned reflection
  // prompts, Spec 104), same reasoning `protocolCompliance`'s doc comment
  // above uses to justify not folding into `compliance`.
  journalPrompts: {
    // GET /organizations/:orgId/journal-prompts -- confirmed via
    // journalPromptRoutes.ts/journalPromptService.getPrompts:
    // "role-filtered: admin sees all, member sees own" (isDominant flag
    // resolved server-side per caller). PromptBanner.tsx only ever fetches
    // as the non-admin branch (its own isAdmin check short-circuits the
    // query via `enabled`), so this is genuinely "my own assigned prompts"
    // for its one live consumer today. Scoped by userId per this batch's
    // mandatory discipline -- AuthContext's login()/register() don't
    // queryClient.clear(), so a same-tab account switch between two
    // submissives in the same org could otherwise serve one's cached
    // prompt list to the other. status folds into the key (mirrors
    // channelChallenges.list's shape) for a future filtered caller, even
    // though PromptBanner.tsx itself always passes undefined (all statuses).
    list: (orgId: string, userId: string, status?: string) =>
      ['journal-prompts', orgId, userId, status ?? null] as const,
    // GET /organizations/:orgId/journal-prompts/templates
    // (journalPromptAPI.getTemplates, PromptAssignModal.tsx). Confirmed via
    // the route handler: ignores the caller entirely and returns
    // journalPromptService.getPromptTemplates()'s static built-in list --
    // no userId scoping needed (identical for every caller in every org).
    // orgId kept in the key only to mirror the URL shape; not load-bearing.
    templates: (orgId: string) => ['journal-prompts', 'templates', orgId] as const,
  },
  // Spec 297 Phase B batches B10+B11 (checkin-templates/TaskCheckinSection.tsx).
  // GET /checkin-templates/:id (Spec 215) -- a template row (system or
  // org-owned), access-checked per request by requireCheckinTemplateAccess
  // but not per-viewer FILTERED content -- every viewer with read access to
  // a given templateId sees the identical prompts/name/description. No
  // userId scoping needed (not "my own account" data, see this file's
  // userId-scoping rule) -- mirrors chastity.healthChecks(lockId)'s
  // entity-by-id precedent.
  checkinTemplates: {
    detail: (templateId: string) => ['checkin-templates', 'detail', templateId] as const,
    // 215-D1 audited responses read (CheckinResponsesView.tsx) — per-task,
    // not per-template (a given template has one response set per task).
    responses: (taskId: string) => ['checkin-templates', 'responses', taskId] as const,
  },
  // Spec 297 Phase C (Track H, elevated caution -- one-file-per-merge admin
  // pages). New `admin.*` namespace for super-admin-only, platform-wide
  // catalog/config surfaces -- distinct from the existing bare
  // `adminAuditLogs`/`messageReports`-style top-level keys above, which
  // predate this namespace and are left as-is (no value in a mechanical
  // rename of already-shipped keys). Admin surfaces are super-admin-only and
  // generally platform-wide (not per-org/per-user), so unlike most of this
  // file's namespaces they don't default to needing orgId/userId scoping --
  // each admin sub-namespace should still confirm that per its own backend
  // query, same as any other key here.
  admin: {
    // AdminOrganizationCategoriesPage.tsx (file 1, super_admin-only CRUD for
    // the hierarchy/discovery-browsing category taxonomy). GET
    // /admin/organization-categories + /admin/organization-categories/stats
    // -- confirmed via organizationCategoryAdminController.ts: no orgId/userId
    // param anywhere, a single global list every super_admin sees identically.
    // NOT a candidate for the cross-user cache-leak class fixed elsewhere in
    // this migration (e.g. AuthContext.tsx, negotiations.active) -- this is
    // genuinely global, org-independent data, not "my own account" state.
    organizationCategories: {
      list: () => ['admin', 'organization-categories', 'list'] as const,
      stats: () => ['admin', 'organization-categories', 'stats'] as const,
    },
    // AdminInventoryTypesPage.tsx (file 2). NOT org-scoped: confirmed via
    // backend (inventoryController.ts's getAllItemTypesAdmin ->
    // inventoryService.getAllItemTypes(), no orgId/userId param, no
    // per-caller filter in the query) -- item types are a single global
    // catalog shared by every organization on the platform, same reasoning
    // as organizationCategories above. requireSuperAdmin-gated
    // (backend/src/routes/inventoryRoutes.ts), so the bare key is safe --
    // only one global list ever exists, no per-org variant to accidentally
    // collide with.
    inventoryTypes: {
      list: () => ['admin', 'inventory-types'] as const,
    },
    // super-admin-metrics/useSuperAdminMetrics.ts (file 3). GET
    // /admin/dashboard/{overview,users,tasks,media,revenue} -- confirmed via
    // adminController.ts/dashboardService: every handler reads platform-wide
    // aggregates with no req.user scoping (super_admin platform-role-gated
    // route, not "my own account" data). Bundled into ONE query (mirrors the
    // pre-migration loadData()'s single Promise.all() over all 5 endpoints +
    // single loading/error pair) rather than 5 independent queries --
    // SuperAdminMetricsPage.tsx only ever consumes the AGGREGATE
    // loading/error from this hook, never a per-section state, so splitting
    // would add complexity with no behavioral payoff. `dateRange` folds into
    // the key -- 2 of the 5 sub-fetches (users/tasks) take it as a request
    // param, so a different dateRange is genuinely a different cache entry.
    superAdminMetrics: {
      dashboard: (dateRange: number) => ['admin', 'super-admin-metrics', 'dashboard', dateRange] as const,
      // Base/prefix key -- use for invalidation, NOT fetching. Invalidating
      // with the fully-qualified dashboard(dateRange) key only dirties the
      // CURRENTLY-viewed dateRange's cache entry; with staleTime:Infinity,
      // any other cached dateRange variant (a user commonly toggles between
      // several) never gets a background refetch and silently serves
      // pre-mutation data forever after a backfill/aggregate action. Mirrors
      // the prefix-key invalidation convention used throughout this file
      // (e.g. helm-dashboard's own orgId-scoped base key).
      dashboardAll: () => ['admin', 'super-admin-metrics', 'dashboard'] as const,
      // GET /admin/dashboard/{revenue-details,system-health,audit-summary}
      // -- same platform-GLOBAL reasoning, independent of dateRange
      // (confirmed: none of the 3 handlers take a range param). Bundled
      // into ONE query for the same reason as `dashboard` above --
      // RevenueTab/SystemHealthTab/AuditTab all consume the single
      // `advancedLoading` flag, not per-tab loading state.
      advanced: () => ['admin', 'super-admin-metrics', 'advanced'] as const,
    },
    // useMonitoringDashboard.ts (Spec 310) -- one query per tab endpoint,
    // deliberately NOT bundled like superAdminMetrics above: these five
    // monitoring endpoints are independent and must degrade independently
    // (a slow-queries failure shouldn't blank the alerts tab).
    monitoring: {
      dashboard: (hours: number) => ['admin', 'monitoring', 'dashboard', hours] as const,
      metrics: (hours: number) => ['admin', 'monitoring', 'metrics', hours] as const,
      alerts: (limit: number) => ['admin', 'monitoring', 'alerts', limit] as const,
      database: () => ['admin', 'monitoring', 'database'] as const,
      slowQueries: () => ['admin', 'monitoring', 'slow-queries'] as const,
      mediaWorkers: () => ['admin', 'monitoring', 'media-workers'] as const,
    },
    // useAdminDashboard.ts (file 4) -- the platform User Admin Dashboard.
    // UNLIKE organizationCategories/inventoryTypes above, this list IS
    // per-viewer-filtered (search/role/status/verified/date-range/sort/page,
    // all URL-backed) -- confirmed via adminUserController.ts's getAllUsers:
    // every param feeds the WHERE/ORDER BY/LIMIT clause server-side. Folding
    // all 9 into the key mirrors `loadUsers`'s own useCallback dependency
    // array exactly (same "these ARE genuinely different cache entries"
    // reasoning as file 3's dateRange) -- a page-2 fetch must not serve
    // page-1's cached rows, and a search-filtered view must not serve the
    // unfiltered list's cache.
    users: {
      list: (params: {
        search: string;
        roleFilter: string;
        statusFilter: string;
        verifiedFilter: string;
        createdAfter: string;
        createdBefore: string;
        sortBy: string;
        sortOrder: string;
        page: number;
      }) => ['admin', 'users', 'list', params] as const,
      // Base/prefix key -- mutations (status change, delete, create)
      // invalidate THIS, not a fully-qualified list() key, so every cached
      // filter/page variant refetches, not just the one currently on screen.
      // Real HIGH finding on file 3: invalidating a param-specific key only
      // refetches the single visible variant, leaving every other cached
      // page/filter combination stale.
      all: () => ['admin', 'users'] as const,
    },
    // Create-user modal's organization dropdown (adminAPI.getOrganizationsForSelect).
    // Deliberately NOT nested under `users` above (i.e. not `['admin',
    // 'users', ...]`) -- admin.users.all()'s invalidation on every user
    // mutation would otherwise cascade into this via TanStack's prefix
    // matching and refetch the dropdown on every suspend/delete/create,
    // an unrequested cadence change (same "avoid unintended coupling, use a
    // fully distinct top-level key" reasoning as quickPraise's doc comment
    // above). Gated `enabled: showCreateModal` by its consumer -- see
    // QuickPraiseModal.tsx / LapseReportModal.tsx / TemplateLibrary.tsx /
    // QuickCreateModal.tsx for the same `enabled: isOpen`-tied-to-a-modal
    // house pattern.
    usersOrgOptions: () => ['admin', 'users-org-options'] as const,
    // AdminMarketingPage.tsx (file 5, Spec 297 Phase C / Track H). Seven
    // largely-independent tabs (Overview/Blog/Social/Reddit/Followers/
    // Analytics/Settings -- Settings is static, no fetch), each its own
    // sub-namespace so a reviewer can audit one tab's invalidation
    // correctness without reading the others. requireSuperAdmin-gated
    // (marketingAdminRoutes.ts -- "Auth guards (authenticateToken +
    // requireSuperAdmin) applied by parent adminRoutes.ts"), same pattern as
    // organizationCategories/inventoryTypes above.
    marketing: {
      // OverviewTab's stats card (blog draft/review counts + pending
      // mentions + reddit queue counts) -- bundles 4 independent,
      // individually-caught API calls into ONE query, mirroring file 3's
      // superAdminMetrics "single Promise.all(), single loading flag"
      // precedent: pre-migration's fetchStats() set one `loading` flag after
      // all 4 settled regardless of individual failures.
      overviewStats: () => ['admin', 'marketing', 'overview-stats'] as const,
      // OverviewTab's "Next Up to Post" queue card. Deliberately a separate
      // query from overviewStats above (own `queueLoading` flag pre-
      // migration, refetched independently by the Generate Now action --
      // see the `overviewQueue` invalidation in useAdminMarketingOverview.ts).
      overviewQueue: () => ['admin', 'marketing', 'overview-queue'] as const,
      blog: {
        list: (params: { status: string }) => ['admin', 'marketing', 'blog', 'list', params] as const,
        // Base/prefix key for `list` above -- mutations invalidate THIS, not
        // a fully-qualified list() key, so every cached status-filter
        // variant refetches (same admin.users.all() reasoning).
        listAll: () => ['admin', 'marketing', 'blog', 'list'] as const,
        // Deliberately a separate top-level branch from `list`/`listAll`
        // (not nested under them) so invalidating the list doesn't also
        // force a detail refetch for mutations that don't touch the
        // currently-viewed detail (e.g. createBlogPost) -- mutations that
        // need both (update/publish/reject/revise) invalidate `listAll()`
        // AND `detail(id)` explicitly, two calls, not one broad prefix.
        detail: (id: number) => ['admin', 'marketing', 'blog', 'detail', id] as const,
      },
      // SocialTab's mention moderation queue. No status filter/pagination in
      // the UI (always limit:50) -- a bare key, same reasoning as
      // organizationCategories.list() above.
      mentions: () => ['admin', 'marketing', 'mentions'] as const,
      // RedditTab's interaction moderation queue -- same shape as mentions.
      reddit: () => ['admin', 'marketing', 'reddit'] as const,
      followers: {
        list: (params: { page: number; tier: string }) => ['admin', 'marketing', 'followers', 'list', params] as const,
        // Base/prefix key -- tier-override mutations invalidate THIS so
        // every cached page/tier variant refetches, not just the
        // currently-viewed one (real HIGH finding class on this phase's
        // file 3, same fix applied proactively here).
        listAll: () => ['admin', 'marketing', 'followers', 'list'] as const,
      },
      // AnalyticsTab's latest weekly/monthly report. No params (always "the
      // latest" one).
      latestReport: () => ['admin', 'marketing', 'latest-report'] as const,
    },
    // admin-storage/useAdminStorage.ts (file 7, Spec 297 Phase C / Track H).
    // GET /admin/storage/{config,stats,pending,temp-files,errors} -- bundled
    // into ONE query (mirrors superAdminMetrics.dashboard/marketing.overviewStats'
    // "single Promise.all(), single loading flag" precedent above): the
    // pre-migration loadData() fetched all 5 in one Promise.all and set one
    // shared loading/error pair, with no per-section state anywhere in the
    // page or its 4 tabs. requireSuperAdmin-gated
    // (backend/src/routes/adminRoutes.ts), platform-global data with no
    // orgId/userId param on any of the 5 handlers (confirmed via
    // storageAdminController.ts) -- same "genuinely global, not 'my own
    // account' state" reasoning as organizationCategories/inventoryTypes
    // above, no cross-user cache-leak risk. Bare key, no params -- none of
    // the 5 endpoints take a request-shaping filter (getStorageErrors' limit
    // is a fixed constant in the queryFn, not a UI-driven variable, so it
    // doesn't need to fold into the key).
    storage: {
      data: () => ['admin', 'storage', 'data'] as const,
    },
    // AdminInvitationsPage.tsx (file 6, Spec 297 Phase C / Track H). NOTE:
    // unlike every other sub-namespace here, this page's own frontend gate is
    // `if (!user)` ONLY -- no `role !== 'super_admin'` check -- it relies
    // entirely on backend enforcement (see the page's own "Platform-wide
    // console (super_admin only, enforced server-side)" comment). Preserved
    // as-is per this batch's explicit instruction; query hooks below gate on
    // `enabled: !!user`, not an `isSuperAdmin` flag copied from sibling files.
    invitations: {
      // The org-selector dropdown (adminAPI.getOrganizationsForSelect) --
      // same endpoint as `usersOrgOptions` above, but deliberately its OWN
      // key, not a reuse: `usersOrgOptions` is lazily `enabled: showCreateModal`
      // and has no side effects, while this page fetches unconditionally on
      // mount and drives an auto-select-first-org effect off the result (see
      // useAdminInvitations.ts's header comment) -- same "avoid unintended
      // coupling / differing per-query needs" reasoning as
      // organizations.myList() vs organizations.contextList()'s split above.
      organizations: () => ['admin', 'invitations', 'organizations'] as const,
      // status is 'all' | 'pending' | 'accepted' | 'expired' | 'revoked' --
      // folded into the key (mirrors admin.marketing.followers.list's
      // tier-in-key precedent) so switching the status filter is structurally
      // a fresh cache entry, matching pre-migration's loadInvitations
      // useCallback dependency array exactly.
      list: (orgId: string, status: string) => ['admin', 'invitations', 'list', orgId, status] as const,
      // Base/prefix key -- resend/revoke mutations AND InviteModal's
      // onInviteSent callback invalidate THIS, not a fully-qualified list()
      // key, so every cached status-filter variant for this org refetches,
      // not just the one currently on screen (same admin.users.all() /
      // admin.marketing.followers.listAll() reasoning -- a real HIGH finding
      // class on this phase's earlier files, applied proactively here).
      listForOrg: (orgId: string) => ['admin', 'invitations', 'list', orgId] as const,
    },
    // useAdminOrganizations.ts (file 9, Spec 297 Phase C / Track H). GET
    // /admin/organizations -- genuinely per-viewer-filtered (search/
    // subscriptionLevel/memberCountMin/memberCountMax/createdAfter/
    // createdBefore/sortBy/sortOrder/page, all URL-backed) -- confirmed via
    // adminOrgController.ts's getAllOrganizations: every param feeds the
    // WHERE/ORDER BY/LIMIT clause server-side, same "these ARE genuinely
    // different cache entries" reasoning as admin.users.list's file 4
    // precedent -- a page-2 fetch must not serve page-1's cached rows, and a
    // search-filtered view must not serve the unfiltered list's cache.
    organizations: {
      list: (params: {
        search: string;
        subscriptionLevel: string;
        memberCountMin: string;
        memberCountMax: string;
        createdAfter: string;
        createdBefore: string;
        sortBy: string;
        sortOrder: string;
        page: number;
      }) => ['admin', 'organizations', 'list', params] as const,
      // Base/prefix key -- the 4 mutations (grant/revoke subscription,
      // create, delete organization) invalidate THIS, not a fully-qualified
      // list() key, so every cached filter/page variant refetches, not just
      // the one currently on screen (same admin.users.all() /
      // admin.marketing.followers.listAll() reasoning -- a real HIGH finding
      // class on earlier files in this phase, applied proactively here).
      //
      // File 10 (useAdminUserDetail.ts) also reuses THIS exact key for its
      // own unfiltered "Add to Organization" dropdown fetch (no params
      // call) -- see that hook's own doc comment where it's used: an org
      // created/deleted via this page's mutations correctly invalidates
      // that dropdown's cache too, since it's the same base key.
      listAll: () => ['admin', 'organizations', 'list'] as const,
      // Grant Subscription modal's plan dropdown -- global catalog, no
      // params (same organizationCategories/inventoryTypes "genuinely
      // global, not per-viewer" reasoning above).
      plans: () => ['admin', 'organizations', 'plans'] as const,
      // Create Organization modal's Add Admins/Add Members boxes' one-time
      // top-500 snapshot -- the INITIAL/empty-query list only. The ad-hoc
      // typeahead server search (`searchUsers`, called imperatively as the
      // admin types) deliberately stays a plain async function, NOT a
      // query/mutation -- it's a one-off call triggered by keystrokes, not
      // declarative subscribed state a component renders from -- so it has
      // no key here.
      users: () => ['admin', 'organizations', 'users'] as const,
      // Grant/Manage modal's member list. orgId-scoped: switching which
      // org's modal is open must not serve a different org's cached list.
      members: (orgId: string) => ['admin', 'organizations', 'members', orgId] as const,
    },
    // AdminAIModerationPage.tsx (file 8, Spec 297 Phase C / Track H). Same
    // requireSuperAdmin gate as every sibling admin.* namespace above --
    // moderationRoutes.ts has NO middleware of its own (confirmed by
    // reading the file; its own header comment says so explicitly), it
    // inherits `router.use(authenticateToken); router.use(requireSuperAdmin);`
    // from the PARENT adminRoutes.ts (lines 118-119), positioned above the
    // `router.use('/moderation', moderationRoutes)` mount at line 196.
    //
    // Structural note (why this namespace's shape differs from `marketing`/
    // `invitations` above): this page is a SINGLE monolithic component --
    // its 3 tabs (queue/stats/log) are inline `{activeTab === 'x' && (...)}`
    // JSX branches, not separately-mounted subcomponents like
    // AdminMarketingPage.tsx's OverviewTab/BlogTab/etc. That matters for
    // cache freshness: sibling files' tabs naturally remount (and thus
    // re-trigger `refetchOnMount:'always'`) on every tab switch because the
    // whole subtree unmounts/remounts. Here the query hooks live in the
    // page's top-level render and never unmount when a tab is hidden --
    // toggling a query's `enabled` from false->true on an ALREADY-fetched,
    // `staleTime:Infinity` cache entry does NOT trigger a refetch (confirmed
    // by reading query-core's queryObserver.ts: `shouldFetchOptionally`
    // requires `isStale`, which is false whenever `staleTime` is Infinity
    // and data already exists -- `refetchOnMount` only fires on a literal
    // Subscribable mount, not an `enabled` flip within the same mounted
    // observer). So `refetchOnMount:'always'` alone would silently no-op on
    // a tab revisit here, unlike the marketing page. useAdminAIModeration.ts
    // instead reproduces the pre-migration effect's "fires on every tab
    // switch, including revisits" behavior with an explicit `.refetch()`
    // call gated by a visited-tabs ref (see that file's header comment) --
    // the FIRST visit to a tab is still covered by the normal
    // enabled:false->true auto-fetch (a query with no data yet is always
    // stale regardless of staleTime), so the explicit refetch only fires on
    // the 2nd+ visit, avoiding a double-fetch on first mount.
    //
    // The backend's `authorizeModerationTransition` (transitionGuard.ts)
    // re-checks the item's CURRENT final_action server-side before allowing
    // any resolve transition, rejecting an action on an already-resolved
    // item rather than silently succeeding -- this reduces (does not
    // eliminate) the severity of any residual staleness window between a
    // tab revisit and the guard's own DB read.
    moderation: {
      queue: (page: number) => ['admin', 'moderation', 'queue', page] as const,
      // Base/prefix key -- the resolve mutation invalidates THIS, not a
      // fully-qualified queue(page) key, so every cached page refetches, not
      // just the one currently on screen (same admin.users.all() /
      // admin.marketing.followers.listAll() reasoning applied proactively).
      queueAll: () => ['admin', 'moderation', 'queue'] as const,
      stats: () => ['admin', 'moderation', 'stats'] as const,
      log: (page: number, actionFilter: string) => ['admin', 'moderation', 'log', page, actionFilter] as const,
      // Base/prefix key -- not currently invalidated by any mutation in this
      // file (pre-migration's handleResolve only ever reloaded the queue,
      // never the log or stats -- preserved as-is), but provided for
      // parity/future use, same as marketing's per-tab `listAll`/`dashboardAll`
      // precedents.
      logAll: () => ['admin', 'moderation', 'log'] as const,
    },
    // useAdminUserDetail.ts (file 10, Spec 297 Phase C / Track H -- LAST
    // file, deliberately ordered last as the most sensitive: a single-target
    // admin detail/edit page with cross-tenant individual-user PII
    // view/edit: password reset, age-verification override, org
    // membership/role changes, email verification, impersonation trigger).
    // Bundles GET /admin/users/:id + GET /admin/users/:id/organizations into
    // ONE query (mirrors this phase's file 3/7/8 "one query when
    // pre-migration treats a page's on-mount load as one Promise.all() unit"
    // precedent) -- pre-migration's loadUserDetails() fetched both under one
    // try/catch/loading pair, and every one of the 6 reload-triggering
    // mutations below re-ran BOTH via a single `await loadUserDetails()`
    // call, never just one half.
    //
    // Deliberately its OWN top-level key, NOT nested under `admin.users`
    // (the file 4 platform User Admin Dashboard's LIST namespace above): a
    // different queryFn shape entirely (one target user's full detail +
    // their orgs, vs. a paginated/filtered roster), and nesting under
    // `admin.users`'s `['admin','users']` prefix would mean file 4's own
    // status/role/delete mutations (which invalidate `admin.users.all()`,
    // the bare `['admin','users']` prefix) would ALSO invalidate this page's
    // cache entry via TanStack's prefix matching -- an unrequested coupling
    // pre-migration never had (the two pages were fully independent
    // component trees, never cross-invalidated). Same "avoid unintended
    // coupling, use a fully distinct top-level key" call as
    // `identity.registrationResume`'s doc comment elsewhere in this file.
    //
    // `id` is the target user's public UUID (useParams, resolved
    // server-side) -- genuinely varies per page visit (a different id is a
    // different user's PII), so it's the sole key segment.
    userDetail: {
      detail: (id: string) => ['admin', 'user-detail', id] as const,
    },
    // Spec 308 (Trust Level Moderation UI). Its own top-level `admin.*`
    // sub-namespace, not nested under `userDetail` -- rateLimits() is
    // platform-wide config with no per-user id, and user()'s per-target
    // trust/history query is fetched independently of the rest of
    // userDetail.detail(id) (its own GET, its own refetch cadence), so
    // nesting either under the other would create an unrequested
    // invalidation coupling (same "avoid unintended coupling" reasoning as
    // userDetail's own doc comment above).
    adminTrustLevel: {
      user: (id: string) => ['admin', 'trust-level', 'user', id] as const,
      rateLimits: () => ['admin', 'trust-level', 'rate-limits'] as const,
    },
  },
} as const;
